import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, Play, Pause, Square, SkipBack, SkipForward,
  Circle, Volume2, VolumeX, Plus, Trash2, Music, 
  ChevronDown, ChevronRight, Save, Download, Mic,
  FileText, X, Wand2, RotateCcw, Settings, Repeat,
  Keyboard, Layers, ZoomIn, ZoomOut, Grid, Piano,
  Sliders, SlidersHorizontal, Clock, Activity,
  Undo2, Redo2, Package, FolderPlus, Hand, FolderOpen, FilePlus,
  Copy, Scissors, ClipboardPaste, MousePointer2, Crosshair
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import * as Tone from 'tone';
import { useAuth } from '../../context/AuthContext';

// Import DAW Components from extracted modules
import { TrackListItem } from './components/daw/TrackListItem';
import { ArrangementView } from './components/daw/ArrangementView';
import { EffectsPanel } from './components/daw/EffectsPanel';
import { MixerSection } from './components/daw/MixerSection';
import { Modal } from './components/daw/Modal';
import { ScoreViewModal } from './components/daw/ScoreViewModal';
import { InstrumentPackageBuilder } from './components/daw/InstrumentPackageBuilder';
import { FloatingTransport } from './components/daw/FloatingTransport';
import { 
  NOTE_NAMES, OCTAVES, ALL_NOTES, 
  INSTRUMENT_LIBRARY, CATEGORIES, KEYBOARD_MAP,
  PIANO_KEYBOARD_MAP, REGIONS
} from './components/daw/constants';
import { MidiRecordingPanel } from './components/daw/MidiRecordingPanel';

// ============================================================================
// MAIN DAW COMPONENT
// ============================================================================
const AdminScoreCreator = () => {
  // Auth context for token management
  const { token, loading: authLoading, isAuthenticated } = useAuth();
  
  // Project
  const [projectName, setProjectName] = useState('Untitled Project');
  const [projectId, setProjectId] = useState(null); // Current project ID if loaded
  const [tempo, setTempo] = useState(120);
  const [timeSignature, setTimeSignature] = useState('4/4');
  const [measures, setMeasures] = useState(64); // Start with 64 measures, can expand infinitely
  const [isSaving, setIsSaving] = useState(false);
  const [showProjectsModal, setShowProjectsModal] = useState(false);
  const [savedProjects, setSavedProjects] = useState([]);
  
  // Transport
  const [isPlaying, setIsPlaying] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [position, setPosition] = useState('1.1.1');
  const [metronomeOn, setMetronomeOn] = useState(false);
  const [loopOn, setLoopOn] = useState(false);
  const [loopStart, setLoopStartRaw] = useState(1); // Loop start measure
  const [loopEnd, setLoopEndRaw] = useState(4); // Loop end measure
  
  // Wrapper for setLoopEnd that auto-expands measures
  const setLoopEnd = (newEnd) => {
    setLoopEndRaw(newEnd);
    if (newEnd > measures) {
      setMeasures(newEnd + 4); // Auto-expand with buffer
    }
    if (loopOn) {
      Tone.Transport.loopEnd = `${newEnd}:0:0`;
    }
  };
  
  const setLoopStart = (newStart) => {
    setLoopStartRaw(newStart);
    if (loopOn) {
      Tone.Transport.loopStart = `${newStart - 1}:0:0`;
    }
  };
  
  // Floating Transport Window
  const [showFloatingTransport, setShowFloatingTransport] = useState(true); // Start visible
  const [floatingTransportPos, setFloatingTransportPos] = useState({ x: window.innerWidth - 400, y: 100 }); // Start on the right
  const [floatingTransportSize, setFloatingTransportSize] = useState({ width: 320, height: 80 });
  
  // View
  const [viewMode, setViewMode] = useState('arrangement'); // 'arrangement' or 'pianoroll'
  const [zoom, setZoom] = useState(1);
  const [showEffectsPanel, setShowEffectsPanel] = useState(false);
  const [showMixer, setShowMixer] = useState(true);
  
  // Tracks
  const [tracks, setTracks] = useState([]);
  const [selectedTrackId, setSelectedTrackId] = useState(null);
  
  // Undo/Redo History
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const isUndoRedoAction = useRef(false);
  
  // Modals
  const [showScoreView, setShowScoreView] = useState(false);
  const [showAutoGenerate, setShowAutoGenerate] = useState(false);
  const [showPackageBuilder, setShowPackageBuilder] = useState(false);
  
  // Instrument Packages
  const [instrumentPackages, setInstrumentPackages] = useState([]);
  
  // Kit Samples from Kit Sample Manager
  const [kitSamples, setKitSamples] = useState([]);
  
  // MIDI Recording Mode
  const [midiRecordingMode, setMidiRecordingMode] = useState(false);
  const [midiQuantize, setMidiQuantize] = useState(true); // Toggle: quantize to grid or free
  const [midiInputDevice, setMidiInputDevice] = useState(null);
  const [availableMidiDevices, setAvailableMidiDevices] = useState([]);
  const [showMidiPanel, setShowMidiPanel] = useState(false);
  const [activeKeys, setActiveKeys] = useState(new Set()); // Currently pressed keys
  const recordedNotesRef = useRef([]); // Notes recorded during recording session
  const recordStartTimeRef = useRef(null);
  
  // KEYBOARD_MAP is imported from './components/daw/constants'
  
  // Audio
  const [loadedInstruments, setLoadedInstruments] = useState({});
  const [loadingInstrument, setLoadingInstrument] = useState(null);
  const samplersRef = useRef({});
  const metronomeRef = useRef(null);
  
  // User samples
  const [userSamples, setUserSamples] = useState({});
  const [loadingUserSamples, setLoadingUserSamples] = useState(true);
  
  // Sample BPM settings (stored separately to persist across instrument changes)
  const [sampleBpmSettings, setSampleBpmSettings] = useState({}); // { sampleId: { bpm: 120, syncEnabled: true } }
  
  // Effects (per track)
  const effectsRef = useRef({});
  
  const API = process.env.REACT_APP_BACKEND_URL || '';
  const selectedTrack = tracks.find(t => t.id === selectedTrackId);
  
  // ============================================================================
  // UNDO/REDO SYSTEM
  // ============================================================================
  
  // Save state to history (called after any change)
  const saveToHistory = useCallback((newTracks, action = 'change') => {
    if (isUndoRedoAction.current) {
      isUndoRedoAction.current = false;
      return;
    }
    
    const state = {
      tracks: JSON.parse(JSON.stringify(newTracks)),
      selectedTrackId,
      timestamp: Date.now(),
      action
    };
    
    setHistory(prev => {
      // Remove any future history if we're not at the end
      const newHistory = prev.slice(0, historyIndex + 1);
      newHistory.push(state);
      // Keep max 50 history states
      if (newHistory.length > 50) newHistory.shift();
      return newHistory;
    });
    setHistoryIndex(prev => Math.min(prev + 1, 49));
  }, [historyIndex, selectedTrackId]);
  
  // Undo
  const undo = useCallback(() => {
    if (historyIndex <= 0) {
      toast.info('Nothing to undo');
      return;
    }
    
    isUndoRedoAction.current = true;
    const prevState = history[historyIndex - 1];
    setTracks(prevState.tracks);
    setSelectedTrackId(prevState.selectedTrackId);
    setHistoryIndex(prev => prev - 1);
    toast.success('Undo: ' + (history[historyIndex]?.action || 'change'));
  }, [history, historyIndex]);
  
  // Redo
  const redo = useCallback(() => {
    if (historyIndex >= history.length - 1) {
      toast.info('Nothing to redo');
      return;
    }
    
    isUndoRedoAction.current = true;
    const nextState = history[historyIndex + 1];
    setTracks(nextState.tracks);
    setSelectedTrackId(nextState.selectedTrackId);
    setHistoryIndex(prev => prev + 1);
    toast.success('Redo: ' + nextState.action);
  }, [history, historyIndex]);
  
  // Keyboard shortcuts for Undo/Redo
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        if (e.shiftKey) {
          redo();
        } else {
          undo();
        }
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'y') {
        e.preventDefault();
        redo();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo]);
  
  // Fetch instrument packages - using AuthContext token
  useEffect(() => {
    if (authLoading) return; // Wait for auth to initialize
    if (!token) {
      console.log('[ScoreCreator] No token from AuthContext, skipping package fetch');
      return;
    }
    
    const fetchPackages = async () => {
      console.log('[ScoreCreator] Fetching packages with AuthContext token');
      
      try {
        const res = await fetch(`${API}/api/instrument-packages`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        console.log('[ScoreCreator] Packages API response:', res.status);
        
        if (res.ok) {
          const data = await res.json();
          console.log('[ScoreCreator] Loaded packages:', data.packages?.length || 0);
          setInstrumentPackages(data.packages || []);
        } else {
          console.error('[ScoreCreator] Failed to load packages:', res.status);
        }
      } catch (e) {
        console.error('[ScoreCreator] Error fetching packages:', e);
      }
    };
    
    fetchPackages();
  }, [API, token, authLoading]);
  
  // Fetch kit samples from Kit Sample Manager - using AuthContext token
  useEffect(() => {
    if (authLoading) return; // Wait for auth to initialize
    if (!token) {
      console.log('[ScoreCreator] No token from AuthContext, skipping kit samples fetch');
      return;
    }
    
    const fetchKitSamples = async () => {
      console.log('[ScoreCreator] Fetching kit samples with AuthContext token');
      
      try {
        const res = await fetch(`${API}/api/kit-samples`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        console.log('[ScoreCreator] Kit samples API response:', res.status);
        
        if (res.ok) {
          const data = await res.json();
          console.log('[ScoreCreator] Loaded kit samples:', data.samples?.length || 0);
          setKitSamples(data.samples || []);
        }
      } catch (e) {
        console.error('[ScoreCreator] Error fetching kit samples:', e);
      }
    };
    
    fetchKitSamples();
  }, [API, token, authLoading]);
  
  // Save project to backend
  const saveProject = async () => {
    if (!token) {
      toast.error('Please log in to save projects');
      return;
    }
    
    setIsSaving(true);
    try {
      const projectData = {
        name: projectName,
        tempo,
        timeSignature,
        measures,
        tracks: tracks.map(t => ({
          id: t.id,
          name: t.name,
          instrumentId: t.instrumentId,
          color: t.color,
          notes: t.notes || [],
          volume: t.volume,
          pan: t.pan,
          muted: t.muted,
          solo: t.solo,
          recordArmed: t.recordArmed,
          isUserSample: t.isUserSample || false,
          isPackage: t.isPackage || false,
          packageId: t.packageId || null,
          sampleUrl: t.sampleUrl || null,
          effects: t.effects || {}
        }))
      };
      
      let res;
      if (projectId) {
        // Update existing project
        res = await fetch(`${API}/api/score-projects/${projectId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(projectData)
        });
      } else {
        // Create new project
        res = await fetch(`${API}/api/score-projects`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(projectData)
        });
      }
      
      const data = await res.json();
      
      if (res.ok) {
        setProjectId(data.project.id);
        toast.success(`Project saved: ${projectName}`);
      } else {
        toast.error(data.detail || 'Failed to save project');
      }
    } catch (e) {
      console.error('Save error:', e);
      toast.error('Failed to save project');
    } finally {
      setIsSaving(false);
    }
  };
  
  // Load list of saved projects
  const loadProjectsList = async () => {
    if (!token) return;
    
    try {
      const res = await fetch(`${API}/api/score-projects`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setSavedProjects(data.projects || []);
      }
    } catch (e) {
      console.error('Load projects error:', e);
    }
  };
  
  // Load a specific project
  const loadProject = async (id) => {
    if (!token) return;
    
    try {
      const res = await fetch(`${API}/api/score-projects/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        const project = data.project;
        
        setProjectId(project.id);
        setProjectName(project.name);
        setTempo(project.tempo);
        setTimeSignature(project.timeSignature);
        setMeasures(project.measures);
        setTracks(project.tracks || []);
        setSelectedTrackId(project.tracks?.[0]?.id || null);
        setShowProjectsModal(false);
        
        toast.success(`Loaded: ${project.name}`);
      }
    } catch (e) {
      console.error('Load project error:', e);
      toast.error('Failed to load project');
    }
  };
  
  // Delete a project
  const deleteProject = async (id) => {
    if (!token) return;
    if (!window.confirm('Are you sure you want to delete this project?')) return;
    
    try {
      const res = await fetch(`${API}/api/score-projects/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        setSavedProjects(prev => prev.filter(p => p.id !== id));
        if (projectId === id) {
          setProjectId(null);
          setProjectName('Untitled Project');
        }
        toast.success('Project deleted');
      }
    } catch (e) {
      console.error('Delete project error:', e);
      toast.error('Failed to delete project');
    }
  };
  
  // Create new project
  const newProject = () => {
    if (tracks.length > 0 && !window.confirm('Create a new project? Unsaved changes will be lost.')) return;
    
    setProjectId(null);
    setProjectName('Untitled Project');
    setTempo(120);
    setTimeSignature('4/4');
    setMeasures(16);
    setTracks([]);
    setSelectedTrackId(null);
    setHistory([]);
    setHistoryIndex(-1);
  };
  
  // Initialize Web MIDI API
  useEffect(() => {
    const initMidi = async () => {
      if (navigator.requestMIDIAccess) {
        try {
          const midiAccess = await navigator.requestMIDIAccess();
          const inputs = Array.from(midiAccess.inputs.values());
          setAvailableMidiDevices(inputs.map(input => ({
            id: input.id,
            name: input.name || 'Unknown MIDI Device'
          })));
          
          // Listen for new devices
          midiAccess.onstatechange = (e) => {
            const inputs = Array.from(midiAccess.inputs.values());
            setAvailableMidiDevices(inputs.map(input => ({
              id: input.id,
              name: input.name || 'Unknown MIDI Device'
            })));
          };
          
          // Set up MIDI listeners for all inputs
          midiAccess.inputs.forEach(input => {
            input.onmidimessage = handleMidiMessage;
          });
        } catch (e) {
          console.log('MIDI access denied or unavailable');
        }
      }
    };
    initMidi();
  }, []);
  
  // Handle MIDI messages
  const handleMidiMessage = useCallback((message) => {
    const [status, note, velocity] = message.data;
    const command = status >> 4;
    
    // Note On (command 9) with velocity > 0
    if (command === 9 && velocity > 0) {
      // Map MIDI note to sub-instrument index (C1 = 36 is index 0)
      const subInstrumentIndex = note - 36;
      if (subInstrumentIndex >= 0) {
        playSubInstrumentByIndex(subInstrumentIndex, velocity / 127);
        if (isRecording && midiRecordingMode) {
          recordNote(subInstrumentIndex);
        }
      }
    }
  }, [isRecording, midiRecordingMode]);
  
  // Computer keyboard handling for MIDI-style input
  useEffect(() => {
    if (!midiRecordingMode) return;
    
    const handleKeyDown = async (e) => {
      // Don't capture if typing in an input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      
      // Check if this is for a drum kit (package) or melodic instrument
      if (selectedTrack?.isPackage) {
        // Drum kit handling
        const index = KEYBOARD_MAP[e.code];
        if (index !== undefined && !activeKeys.has(e.code)) {
          e.preventDefault();
          setActiveKeys(prev => new Set([...prev, e.code]));
          playSubInstrumentByIndex(index, 0.8);
          if (isRecording) {
            recordNote(index);
          }
        }
      } else if (selectedTrack && !selectedTrack.isPackage) {
        // Melodic instrument handling - use piano keyboard map
        const note = PIANO_KEYBOARD_MAP[e.code];
        if (note && !activeKeys.has(e.code)) {
          e.preventDefault();
          setActiveKeys(prev => new Set([...prev, e.code]));
          
          // Play the note on the sampler/synth
          const sampler = samplersRef.current[selectedTrack.instrumentId];
          if (sampler) {
            await Tone.start();
            sampler.triggerAttack(note, undefined, 0.8);
          }
          
          // Record the note if recording
          if (isRecording && recordStartTimeRef.current) {
            const currentTime = Tone.now() - recordStartTimeRef.current;
            const beatsPerSecond = tempo / 60;
            let beat = currentTime * beatsPerSecond;
            
            // Quantize if enabled
            if (midiQuantize) {
              const grid = 0.25; // 16th notes
              beat = Math.round(beat / grid) * grid;
            }
            
            recordedNotesRef.current.push({
              note: note,
              pitch: note,
              beat: beat,
              measure: Math.floor(beat / timeSignature[0]),
              duration: 0.25, // 16th note default
              velocity: 0.8,
              timestamp: currentTime
            });
          }
        }
      }
    };
    
    const handleKeyUp = (e) => {
      // Drum kit key release
      if (KEYBOARD_MAP[e.code] !== undefined) {
        setActiveKeys(prev => {
          const next = new Set(prev);
          next.delete(e.code);
          return next;
        });
      }
      
      // Melodic instrument key release
      if (PIANO_KEYBOARD_MAP[e.code]) {
        setActiveKeys(prev => {
          const next = new Set(prev);
          next.delete(e.code);
          return next;
        });
        
        // Release the note
        if (selectedTrack && !selectedTrack.isPackage) {
          const note = PIANO_KEYBOARD_MAP[e.code];
          const sampler = samplersRef.current[selectedTrack.instrumentId];
          if (sampler && sampler.triggerRelease) {
            sampler.triggerRelease(note);
          }
        }
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [midiRecordingMode, activeKeys, isRecording, selectedTrack, tempo, midiQuantize, timeSignature]);
  
  // Play a sub-instrument by index (from the selected track's package)
  const playSubInstrumentByIndex = useCallback((index, velocity = 0.8) => {
    if (!selectedTrack) return;
    
    // Check if track has a package assigned
    const packageId = selectedTrack.packageId;
    if (!packageId) {
      // If no package, treat as single instrument track
      const sampler = samplersRef.current[selectedTrack.instrumentId];
      if (sampler) {
        sampler.triggerAttackRelease('C4', '8n', undefined, velocity);
      }
      return;
    }
    
    // Get all samples for this kit, sorted by filename for consistent mapping
    const pkgSamples = kitSamples
      .filter(s => s.kit_id === packageId)
      .sort((a, b) => (a.filename || '').localeCompare(b.filename || ''));
    
    if (pkgSamples.length === 0) {
      console.log(`No samples found for package: ${packageId}`);
      return;
    }
    
    // Map the index to a sample (wrap around if index > sample count)
    const sampleIndex = index % pkgSamples.length;
    const sample = pkgSamples[sampleIndex];
    
    if (!sample) return;
    
    const sampleUrl = `${API}${sample.url}`;
    const playerId = `kit:${sample.id}`;
    
    console.log(`Playing sample ${sampleIndex}: ${sample.filename} from ${packageId}`);
    
    // Create or get player for this sample
    if (!samplersRef.current[playerId]) {
      const player = new Tone.Player(sampleUrl).toDestination();
      player.volume.value = (selectedTrack.volume || 0) - 6;
      samplersRef.current[playerId] = player;
      // Play when loaded
      player.autostart = true;
    } else {
      const player = samplersRef.current[playerId];
      if (player.loaded) {
        player.stop();
        player.start();
      } else {
        // Still loading, play with autostart
        player.autostart = true;
      }
    }
  }, [selectedTrack, kitSamples, API]);
  
  // Record a note during recording mode
  const recordNote = useCallback((subInstrumentIndex) => {
    if (!isRecording || !selectedTrack) return;
    
    const currentTime = Tone.now();
    const startTime = recordStartTimeRef.current || currentTime;
    const elapsed = currentTime - startTime;
    
    // Calculate beat position
    const beatsPerSecond = tempo / 60;
    const beatPosition = elapsed * beatsPerSecond;
    
    // Quantize if enabled
    let quantizedBeat = beatPosition;
    if (midiQuantize) {
      const quantizeGrid = 0.25; // 16th notes
      quantizedBeat = Math.round(beatPosition / quantizeGrid) * quantizeGrid;
    }
    
    // Convert to measure.beat format
    const beatsPerMeasure = parseInt(timeSignature.split('/')[0]) || 4;
    const measure = Math.floor(quantizedBeat / beatsPerMeasure);
    const beat = quantizedBeat % beatsPerMeasure;
    
    recordedNotesRef.current.push({
      subInstrumentIndex,
      measure,
      beat,
      duration: '8n',
      velocity: 0.8,
      timestamp: currentTime
    });
  }, [isRecording, selectedTrack, tempo, midiQuantize, timeSignature]);
  
  // Start recording with MIDI
  const startMidiRecording = useCallback(async () => {
    await Tone.start();
    recordStartTimeRef.current = Tone.now();
    recordedNotesRef.current = [];
    setIsRecording(true);
    Tone.Transport.start();
    toast.success('Recording started - Play your keyboard!');
  }, []);
  
  // Stop recording and commit notes to track
  const stopMidiRecording = useCallback(() => {
    setIsRecording(false);
    Tone.Transport.stop();
    
    if (recordedNotesRef.current.length > 0 && selectedTrack) {
      let newNotes;
      
      if (selectedTrack.isPackage) {
        // Drum kit/package - use PAD notation
        newNotes = recordedNotesRef.current.map((note, i) => ({
          id: `note_${Date.now()}_${i}`,
          pitch: `PAD${note.subInstrumentIndex}`,
          measure: note.measure,
          beat: note.beat,
          duration: note.duration,
          velocity: note.velocity,
          subInstrumentIndex: note.subInstrumentIndex
        }));
      } else {
        // Melodic instrument - use actual note names (C4, D#3, etc)
        newNotes = recordedNotesRef.current.map((note, i) => ({
          id: `note_${Date.now()}_${i}`,
          pitch: note.note || note.pitch,
          measure: note.measure || Math.floor(note.beat / 4),
          beat: note.beat % 4 || note.beat,
          duration: note.duration || 0.25,
          velocity: note.velocity || 0.8
        }));
      }
      
      // Add notes to track
      setTracks(prev => prev.map(t => {
        if (t.id === selectedTrack.id) {
          return {
            ...t,
            notes: [...(t.notes || []), ...newNotes]
          };
        }
        return t;
      }));
      
      toast.success(`Recorded ${newNotes.length} notes!`);
    }
    
    recordedNotesRef.current = [];
    recordStartTimeRef.current = null;
  }, [selectedTrack]);
  
  // Initialize metronome
  useEffect(() => {
    metronomeRef.current = new Tone.MembraneSynth({
      pitchDecay: 0.008,
      octaves: 2,
      envelope: { attack: 0.001, decay: 0.3, sustain: 0, release: 0.1 }
    }).toDestination();
    metronomeRef.current.volume.value = -10;
    
    return () => {
      if (metronomeRef.current) metronomeRef.current.dispose();
    };
  }, []);
  
  // Fetch user samples
  useEffect(() => {
    const fetchUserSamples = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        if (!token) { setLoadingUserSamples(false); return; }
        
        const samplesMap = {};
        
        try {
          const res = await fetch(`${API}/api/admin/media?type=audio&limit=500`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (res.ok) {
            const data = await res.json();
            const audioFiles = data.media || data || [];
            if (Array.isArray(audioFiles)) {
              audioFiles.forEach(media => {
                if (media.type === 'audio' || media.mime_type?.startsWith('audio/')) {
                  const id = media.id || media._id || media.filename;
                  samplesMap[id] = {
                    id,
                    name: media.filename || media.name || id,
                    url: media.url,
                    category: 'My Samples'
                  };
                }
              });
            }
          }
        } catch (e) { console.log('Media fetch:', e.message); }
        
        setUserSamples(samplesMap);
        console.log(`Loaded ${Object.keys(samplesMap).length} user samples`);
      } catch (err) {
        console.error('Failed to fetch samples:', err);
      } finally {
        setLoadingUserSamples(false);
      }
    };
    fetchUserSamples();
  }, [API]);
  
  // Load CDN instrument or create synth
  const loadInstrument = useCallback(async (instrumentId) => {
    if (samplersRef.current[instrumentId]) return samplersRef.current[instrumentId];
    
    const lib = INSTRUMENT_LIBRARY[instrumentId];
    if (!lib) return null;
    
    setLoadingInstrument(instrumentId);
    
    // Handle synth instruments (no samples needed)
    if (lib.isSynth) {
      let synth;
      const options = lib.synthOptions || {};
      
      switch (lib.synthType) {
        case 'FMSynth':
          synth = new Tone.PolySynth(Tone.FMSynth, options).toDestination();
          break;
        case 'AMSynth':
          synth = new Tone.PolySynth(Tone.AMSynth, options).toDestination();
          break;
        case 'DuoSynth':
          synth = new Tone.DuoSynth(options).toDestination();
          break;
        case 'MonoSynth':
          synth = new Tone.MonoSynth(options).toDestination();
          break;
        case 'PluckSynth':
          synth = new Tone.PluckSynth(options).toDestination();
          break;
        case 'MembraneSynth':
          synth = new Tone.MembraneSynth(options).toDestination();
          break;
        case 'MetalSynth':
          synth = new Tone.MetalSynth(options).toDestination();
          break;
        case 'NoiseSynth':
          synth = new Tone.NoiseSynth(options).toDestination();
          break;
        default:
          synth = new Tone.PolySynth(Tone.Synth, options).toDestination();
      }
      synth.volume.value = -6;
      samplersRef.current[instrumentId] = synth;
      setLoadedInstruments(prev => ({ ...prev, [instrumentId]: true }));
      setLoadingInstrument(null);
      return synth;
    }
    
    // Handle sampler instruments
    return new Promise((resolve) => {
      const urls = {};
      for (const [note, file] of Object.entries(lib.samples)) {
        urls[note] = lib.cdnBase + file;
      }
      
      const sampler = new Tone.Sampler(urls, {
        release: 1,
        onload: () => {
          samplersRef.current[instrumentId] = sampler;
          setLoadedInstruments(prev => ({ ...prev, [instrumentId]: true }));
          setLoadingInstrument(null);
          resolve(sampler);
        },
        onerror: () => { setLoadingInstrument(null); resolve(null); }
      }).toDestination();
      sampler.volume.value = -6;
    });
  }, []);
  
  // Load user sample
  const loadUserSample = useCallback(async (sampleId) => {
    const key = `user:${sampleId}`;
    if (samplersRef.current[key]) {
      console.log(`Sample already loaded: ${key}`);
      return samplersRef.current[key];
    }
    
    const sampleData = userSamples[sampleId];
    if (!sampleData?.url) {
      console.error(`No URL for sample: ${sampleId}`);
      toast.error(`Sample not found: ${sampleId}`);
      return null;
    }
    
    setLoadingInstrument(sampleId);
    
    return new Promise((resolve) => {
      const fullUrl = sampleData.url.startsWith('http') ? sampleData.url : `${API}${sampleData.url}`;
      console.log(`Loading sample: ${fullUrl}`);
      
      const player = new Tone.Player({
        url: fullUrl,
        onload: () => {
          console.log(`Sample loaded successfully: ${sampleData.name}`);
          samplersRef.current[key] = player;
          setLoadedInstruments(prev => ({ ...prev, [key]: true }));
          setLoadingInstrument(null);
          toast.success(`Loaded: ${sampleData.name}`);
          resolve(player);
        },
        onerror: (err) => {
          console.error(`Failed to load sample: ${sampleData.name}`, err);
          toast.error(`Failed to load: ${sampleData.name}`);
          setLoadingInstrument(null);
          resolve(null);
        }
      }).toDestination();
      
      player.volume.value = -6;
    });
  }, [userSamples, API]);
  
  // Initialize effects for a track
  const initTrackEffects = (trackId) => {
    if (effectsRef.current[trackId]) return;
    
    effectsRef.current[trackId] = {
      reverb: new Tone.Reverb({ decay: 2, wet: 0 }).toDestination(),
      delay: new Tone.FeedbackDelay({ delayTime: 0.25, feedback: 0.3, wet: 0 }).toDestination(),
      eq: new Tone.EQ3({ low: 0, mid: 0, high: 0 }).toDestination(),
    };
  };
  
  // Add track
  const addTrack = async (instrumentId = 'piano', packageId = null) => {
    const isUserSample = instrumentId.startsWith('user:');
    const isPackage = instrumentId.startsWith('package:') || packageId;
    const actualId = isUserSample ? instrumentId.replace('user:', '') : 
                     isPackage ? instrumentId.replace('package:', '') : instrumentId;
    
    let name, color;
    if (isPackage) {
      const pkgId = packageId || actualId;
      const pkg = instrumentPackages.find(p => p.id === pkgId);
      if (!pkg) {
        toast.error('Package not found');
        return;
      }
      name = pkg.name;
      color = pkg.color || '#8b5cf6';
      
      // Preload samples for this package
      await preloadPackageSamples(pkgId);
    } else if (isUserSample) {
      await loadUserSample(actualId);
      name = userSamples[actualId]?.name || actualId;
      color = '#f97316';
    } else {
      await loadInstrument(instrumentId);
      const lib = INSTRUMENT_LIBRARY[instrumentId];
      name = lib?.name || instrumentId;
      color = lib?.color || '#3b82f6';
    }
    
    const trackId = `track_${Date.now()}`;
    initTrackEffects(trackId);
    
    const newTrack = {
      id: trackId,
      name,
      instrumentId: isPackage ? `package:${packageId || actualId}` : (isUserSample ? `user:${actualId}` : instrumentId),
      packageId: isPackage ? (packageId || actualId) : null,
      instrumentName: name,
      isUserSample,
      isPackage,
      color,
      notes: [],
      clips: [], // For arrangement view
      volume: 0.8,
      pan: 0,
      muted: false,
      solo: false,
      recordArmed: false,
      // Effects wet levels
      reverbWet: 0,
      delayWet: 0,
      eqLow: 0,
      eqMid: 0,
      eqHigh: 0,
      // VU meter
      vuLevel: 0,
      // Sample BPM sync (for user samples)
      sampleBpm: isUserSample ? (sampleBpmSettings[actualId]?.bpm || 120) : null,
      syncToProjectTempo: isUserSample ? (sampleBpmSettings[actualId]?.syncEnabled ?? true) : false
    };
    
    setTracks(prev => {
      const newTracks = [...prev, newTrack];
      saveToHistory(newTracks, `Add track: ${name}`);
      return newTracks;
    });
    setSelectedTrackId(trackId);
    toast.success(`Added: ${name}`);
  };
  
  // Preload samples for a package
  const preloadPackageSamples = async (packageId) => {
    // Get all kit samples for this package
    const pkgSamples = kitSamples.filter(s => s.kit_id === packageId);
    
    console.log(`Preloading ${pkgSamples.length} samples for package: ${packageId}`);
    
    // Preload all samples (up to first 20 for performance)
    const samplesToLoad = pkgSamples.slice(0, 20);
    
    for (const sample of samplesToLoad) {
      const sampleUrl = `${API}${sample.url}`;
      const playerId = `kit:${sample.id}`;
      
      if (!samplersRef.current[playerId]) {
        const player = new Tone.Player(sampleUrl).toDestination();
        player.volume.value = -6;
        samplersRef.current[playerId] = player;
      }
    }
    
    console.log(`Preloaded ${samplesToLoad.length} samples`);
  };
  
  // Change instrument
  const changeTrackInstrument = async (trackId, instrumentId) => {
    const isUserSample = instrumentId.startsWith('user:');
    const isPackage = instrumentId.startsWith('package:');
    const actualId = isUserSample ? instrumentId.replace('user:', '') : 
                     isPackage ? instrumentId.replace('package:', '') : instrumentId;
    
    let name, color, packageId = null;
    
    if (isPackage) {
      const pkg = instrumentPackages.find(p => p.id === actualId);
      if (!pkg) {
        toast.error('Package not found');
        return;
      }
      name = pkg.name;
      color = pkg.color || '#8b5cf6';
      packageId = actualId;
      
      // Preload samples for this package
      await preloadPackageSamples(actualId);
      toast.success(`Loading ${pkg.name} samples...`);
    } else if (isUserSample) {
      console.log(`Changing to user sample: ${actualId}`);
      const loaded = await loadUserSample(actualId);
      if (!loaded) {
        console.error(`Failed to load sample: ${actualId}`);
      }
      name = userSamples[actualId]?.name || actualId;
      color = '#f97316';
    } else {
      await loadInstrument(instrumentId);
      const lib = INSTRUMENT_LIBRARY[instrumentId];
      name = lib?.name || instrumentId;
      color = lib?.color || '#3b82f6';
    }
    
    setTracks(prev => {
      const newTracks = prev.map(t => 
        t.id === trackId ? { 
          ...t, 
          name, 
          instrumentId: isPackage ? `package:${actualId}` : (isUserSample ? `user:${actualId}` : instrumentId), 
          instrumentName: name, 
          isUserSample, 
          isPackage,
          packageId,
          color 
        } : t
      );
      saveToHistory(newTracks, `Change to: ${name}`);
      return newTracks;
    });
    toast.success(`Changed to: ${name}`);
  };
  
  // Delete track
  const deleteTrack = (trackId) => {
    const trackName = tracks.find(t => t.id === trackId)?.name || 'track';
    setTracks(prev => {
      const newTracks = prev.filter(t => t.id !== trackId);
      saveToHistory(newTracks, `Delete: ${trackName}`);
      return newTracks;
    });
    if (selectedTrackId === trackId) {
      setSelectedTrackId(tracks.find(t => t.id !== trackId)?.id || null);
    }
    // Clean up effects
    if (effectsRef.current[trackId]) {
      Object.values(effectsRef.current[trackId]).forEach(fx => fx.dispose());
      delete effectsRef.current[trackId];
    }
  };
  
  // Toggle mute/solo/record
  const toggleMute = (trackId) => {
    setTracks(prev => prev.map(t => {
      if (t.id === trackId) {
        const newMuted = !t.muted;
        // Apply to Tone.js player/sampler
        const key = t.instrumentId || t.id;
        if (samplersRef.current[key]) {
          samplersRef.current[key].mute = newMuted;
        }
        return { ...t, muted: newMuted };
      }
      return t;
    }));
  };
  
  const toggleSolo = (trackId) => {
    setTracks(prev => {
      const updated = prev.map(t => t.id === trackId ? { ...t, solo: !t.solo } : t);
      // If any track is soloed, mute all non-soloed tracks
      const anySoloed = updated.some(t => t.solo);
      updated.forEach(t => {
        const key = t.instrumentId || t.id;
        if (samplersRef.current[key]) {
          if (anySoloed) {
            samplersRef.current[key].mute = !t.solo;
          } else {
            samplersRef.current[key].mute = t.muted;
          }
        }
      });
      return updated;
    });
  };
  
  const toggleRecordArm = (trackId) => setTracks(prev => prev.map(t => t.id === trackId ? { ...t, recordArmed: !t.recordArmed } : t));
  
  // Update track volume/pan - NOW APPLIES TO TONE.JS
  const updateTrackVolume = (trackId, volume) => {
    setTracks(prev => prev.map(t => {
      if (t.id === trackId) {
        // Apply to Tone.js player/sampler
        const key = t.instrumentId || t.id;
        if (samplersRef.current[key] && samplersRef.current[key].volume) {
          // Convert 0-1 to dB (-60 to 0)
          const db = volume === 0 ? -Infinity : (volume - 1) * 40;
          samplersRef.current[key].volume.value = db;
        }
        return { ...t, volume };
      }
      return t;
    }));
  };
  
  const updateTrackPan = (trackId, pan) => {
    setTracks(prev => prev.map(t => {
      if (t.id === trackId) {
        // Apply pan to Tone.js - need a panner node
        // For now just update state, pan requires additional setup
        return { ...t, pan };
      }
      return t;
    }));
  };
  
  // Update track effects
  const updateTrackEffect = (trackId, effect, value) => {
    setTracks(prev => prev.map(t => t.id === trackId ? { ...t, [effect]: value } : t));
    
    // Apply to Tone.js effect
    if (effectsRef.current[trackId]) {
      if (effect === 'reverbWet') effectsRef.current[trackId].reverb.wet.value = value;
      if (effect === 'delayWet') effectsRef.current[trackId].delay.wet.value = value;
      if (effect === 'eqLow') effectsRef.current[trackId].eq.low.value = value;
      if (effect === 'eqMid') effectsRef.current[trackId].eq.mid.value = value;
      if (effect === 'eqHigh') effectsRef.current[trackId].eq.high.value = value;
    }
  };
  
  // Update sample BPM settings
  const updateSampleBpm = (trackId, sampleBpm) => {
    setTracks(prev => prev.map(t => t.id === trackId ? { ...t, sampleBpm } : t));
    
    // Store in settings for persistence
    const track = tracks.find(t => t.id === trackId);
    if (track?.isUserSample) {
      const sampleId = track.instrumentId.replace('user:', '');
      setSampleBpmSettings(prev => ({
        ...prev,
        [sampleId]: { ...prev[sampleId], bpm: sampleBpm }
      }));
      
      // Update playback rate if sync is enabled
      if (track.syncToProjectTempo) {
        const player = samplersRef.current[track.instrumentId];
        if (player && sampleBpm > 0) {
          player.playbackRate = tempo / sampleBpm;
        }
      }
    }
  };
  
  // Toggle sample tempo sync
  const toggleSampleSync = (trackId) => {
    setTracks(prev => prev.map(t => {
      if (t.id === trackId) {
        const newSync = !t.syncToProjectTempo;
        
        // Update playback rate
        const player = samplersRef.current[t.instrumentId];
        if (player && t.sampleBpm > 0) {
          player.playbackRate = newSync ? (tempo / t.sampleBpm) : 1;
        }
        
        // Store setting
        if (t.isUserSample) {
          const sampleId = t.instrumentId.replace('user:', '');
          setSampleBpmSettings(prev => ({
            ...prev,
            [sampleId]: { ...prev[sampleId], syncEnabled: newSync }
          }));
        }
        
        return { ...t, syncToProjectTempo: newSync };
      }
      return t;
    }));
  };
  
  // Update track notes
  const updateTrackNotes = (trackId, notes, skipHistory = false) => {
    setTracks(prev => {
      const newTracks = prev.map(t => t.id === trackId ? { ...t, notes } : t);
      if (!skipHistory) {
        saveToHistory(newTracks, 'Edit notes');
      }
      return newTracks;
    });
  };
  
  // Play/Stop
  const togglePlay = async () => {
    if (isPlaying) {
      Tone.Transport.stop();
      Tone.Transport.cancel();
      setIsPlaying(false);
      setIsRecording(false);
      return;
    }
    
    await Tone.start();
    Tone.Transport.bpm.value = tempo;
    Tone.Transport.cancel();
    
    // Apply loop settings
    if (loopOn) {
      Tone.Transport.loop = true;
      Tone.Transport.loopStart = `${loopStart - 1}:0:0`;
      Tone.Transport.loopEnd = `${loopEnd}:0:0`;
    } else {
      Tone.Transport.loop = false;
    }
    
    const beatsPerMeasure = parseInt(timeSignature.split('/')[0]);
    const hasSolo = tracks.some(t => t.solo);
    
    // Schedule metronome
    if (metronomeOn) {
      const totalBeats = measures * beatsPerMeasure;
      for (let b = 0; b < totalBeats; b++) {
        Tone.Transport.schedule((time) => {
          if (metronomeRef.current) {
            metronomeRef.current.triggerAttackRelease(b % beatsPerMeasure === 0 ? 'C3' : 'C4', '16n', time);
          }
        }, `0:${b}`);
      }
    }
    
    // Schedule track notes
    tracks.forEach(track => {
      if (track.muted) return;
      if (hasSolo && !track.solo) return;
      
      // Check if this is a Kit track (package-based)
      const isKitTrack = track.isPackage && track.packageId;
      const isUserSample = track.isUserSample || track.instrumentId.startsWith('user:');
      
      // For Kit tracks, we don't need a single audioSource - each note plays its own sample
      if (isKitTrack) {
        console.log(`Scheduling ${track.notes.length} kit notes for ${track.name}`);
        
        if (track.notes.length === 0) return;
        
        // Get kit samples for this track
        const trackKitSamples = kitSamples.filter(s => s.kit_id === track.packageId);
        
        track.notes.forEach(note => {
          const beat = note.measure * beatsPerMeasure + (note.beat || 0);
          
          Tone.Transport.schedule((time) => {
            try {
              // Find the sample for this note
              let sampleUrl = null;
              if (note.sampleId) {
                const sample = trackKitSamples.find(s => s.id === note.sampleId);
                if (sample) sampleUrl = `${API}${sample.url}`;
              } else if (note.padIndex !== undefined) {
                const sample = trackKitSamples[note.padIndex];
                if (sample) sampleUrl = `${API}${sample.url}`;
              }
              
              if (sampleUrl) {
                // Create and play audio element for each hit with velocity and volume
                const audio = new Audio(sampleUrl);
                const noteVelocity = note.velocity || 0.8;
                const noteVolume = note.noteVolume ?? 1;
                audio.volume = (track.volume || 0.8) * noteVelocity * noteVolume;
                audio.currentTime = 0;
                audio.play().catch(e => console.warn('Kit sample play error:', e));
              }
            } catch (e) {
              console.warn('Kit playback error:', e);
            }
          }, `0:${beat}`);
        });
        
        return; // Skip the regular playback logic for kit tracks
      }
      
      const audioSource = samplersRef.current[track.instrumentId];
      if (!audioSource) {
        console.warn(`No audio source loaded for track: ${track.name} (${track.instrumentId})`);
        toast.error(`Sample not loaded: ${track.name}. Try selecting it again.`);
        return;
      }
      
      // Apply BPM sync for user samples
      if (isUserSample && audioSource.playbackRate !== undefined) {
        if (track.syncToProjectTempo && track.sampleBpm > 0) {
          audioSource.playbackRate = tempo / track.sampleBpm;
        } else {
          audioSource.playbackRate = 1;
        }
      }
      
      console.log(`Scheduling ${track.notes.length} notes for ${track.name}, isUserSample: ${isUserSample}, loaded: ${audioSource.loaded}`);
      
      if (track.notes.length === 0) {
        console.log(`No notes for track: ${track.name}`);
        return;
      }
      
      track.notes.forEach(note => {
        const beat = note.measure * beatsPerMeasure + (note.beat || 0);
        
        Tone.Transport.schedule((time) => {
          try {
            if (isUserSample) {
              // User samples: use Tone.Player
              if (audioSource.loaded) {
                // Stop any current playback and restart
                try {
                  audioSource.stop(time);
                } catch (e) {
                  // Ignore stop errors
                }
                audioSource.start(time);
                console.log(`Playing sample: ${track.name} at beat ${beat}`);
              } else {
                console.warn(`Sample not loaded: ${track.name}`);
              }
            } else {
              // CDN instruments: use Sampler's triggerAttackRelease with velocity
              if (audioSource.loaded) {
                const pitch = note.pitch.replace('/', '');
                const dur = note.duration === 'w' ? '1n' : note.duration === 'h' ? '2n' : note.duration === 'q' ? '4n' : '8n';
                const noteVelocity = note.velocity || 0.8;
                const noteVolume = note.noteVolume ?? 1;
                const finalVolume = noteVelocity * noteVolume * (track.volume || 0.8);
                audioSource.triggerAttackRelease(pitch, dur, time, finalVolume);
              }
            }
          } catch (e) { 
            console.warn('Playback error:', e);
          }
        }, `0:${beat}`);
      });
    });
    
    Tone.Transport.start();
    setIsPlaying(true);
  };
  
  const stopPlayback = () => {
    Tone.Transport.stop();
    Tone.Transport.cancel();
    setIsPlaying(false);
    setIsRecording(false);
    setPosition('1.1.1');
  };
  
  // Fast forward/rewind functions
  const rewind = () => {
    Tone.Transport.position = 0;
    setPosition('1.1.1');
  };
  
  const fastForward = () => {
    const beatsPerMeasure = parseInt(timeSignature.split('/')[0]);
    const currentPos = Tone.Transport.position;
    const parts = currentPos.split(':');
    const currentMeasure = parseInt(parts[0]) + 1;
    const newMeasure = Math.min(measures, currentMeasure + 4);
    Tone.Transport.position = `${newMeasure - 1}:0:0`;
    setPosition(`${newMeasure}.1.1`);
  };
  
  // Toggle loop and set loop region
  const toggleLoop = () => {
    setLoopOn(!loopOn);
    if (!loopOn) {
      // When enabling loop, set up Tone.Transport loop
      Tone.Transport.loop = true;
      Tone.Transport.loopStart = `${loopStart - 1}:0:0`;
      Tone.Transport.loopEnd = `${loopEnd}:0:0`;
    } else {
      Tone.Transport.loop = false;
    }
  };
  
  // Update loop points - auto-expand measures if needed
  const setLoopRegion = (start, end) => {
    setLoopStart(start);
    setLoopEnd(end);
    // Auto-expand measures if loop end exceeds current measures
    if (end > measures) {
      setMeasures(end + 4); // Add some extra room
    }
    if (loopOn) {
      Tone.Transport.loopStart = `${start - 1}:0:0`;
      Tone.Transport.loopEnd = `${end}:0:0`;
    }
  };
  
  const toggleRecord = async () => {
    if (isRecording) {
      setIsRecording(false);
      return;
    }
    
    const armedTrack = tracks.find(t => t.recordArmed);
    if (!armedTrack) {
      toast.error('Arm a track for recording first (R button)');
      return;
    }
    
    setIsRecording(true);
    if (!isPlaying) {
      await togglePlay();
    }
    toast.success('Recording started');
  };
  
  // Auto-generate
  const autoGenerateScore = (style = 'samba') => {
    const beatsPerMeasure = parseInt(timeSignature.split('/')[0]);
    
    if (tracks.length === 0) {
      ['piano', 'bass-electric', 'cello'].forEach((inst, i) => {
        setTimeout(() => addTrack(inst), i * 100);
      });
    }
    
    setTracks(prev => prev.map(track => {
      const newNotes = [];
      for (let m = 0; m < measures; m++) {
        const noteCount = Math.floor(Math.random() * 3) + 1;
        for (let n = 0; n < noteCount; n++) {
          const noteIndex = Math.floor(Math.random() * 12);
          const octave = Math.floor(Math.random() * 3) + 3;
          newNotes.push({
            measure: m,
            beat: Math.floor(Math.random() * beatsPerMeasure),
            pitch: `${NOTE_NAMES[noteIndex]}/${octave}`,
            duration: ['q', 'h', '8'][Math.floor(Math.random() * 3)],
            velocity: 0.7 + Math.random() * 0.3
          });
        }
      }
      return { ...track, notes: newNotes };
    }));
    
    setShowAutoGenerate(false);
    toast.success(`Generated ${style} pattern`);
  };
  
  const startFresh = () => {
    setTracks(prev => prev.map(t => ({ ...t, notes: [] })));
    toast.success('Cleared all notes');
  };
  
  // Load piano on mount
  useEffect(() => { loadInstrument('piano'); }, [loadInstrument]);
  
  return (
    <div className="h-screen flex flex-col bg-[#0a0a0a] text-white overflow-hidden select-none">
      {/* ================================================================ */}
      {/* TRANSPORT BAR */}
      {/* ================================================================ */}
      <header className="h-14 bg-gradient-to-b from-[#1f1f1f] to-[#161616] border-b border-[#333] flex items-center px-4 gap-3 shrink-0">
        {/* Back */}
        <Link to="/admin" className="p-2 hover:bg-white/10 rounded-lg" data-testid="back-btn">
          <ArrowLeft className="w-5 h-5 text-gray-400" />
        </Link>
        
        {/* Project Name */}
        <input
          type="text"
          value={projectName}
          onChange={(e) => setProjectName(e.target.value)}
          className="bg-[#0a0a0a] text-white font-semibold w-44 px-3 py-1.5 rounded-lg border border-[#333] focus:outline-none focus:border-blue-500"
          data-testid="project-name"
        />
        
        <div className="w-px h-8 bg-[#333]" />
        
        {/* Transport Controls */}
        <div className="flex items-center gap-1 bg-[#0a0a0a] rounded-lg p-1 border border-[#333]">
          <button onClick={() => { Tone.Transport.position = 0; setPosition('1.1.1'); }} className="p-2 hover:bg-white/10 rounded" title="Rewind">
            <SkipBack className="w-4 h-4 text-gray-300" />
          </button>
          <button onClick={stopPlayback} className="p-2 hover:bg-white/10 rounded" title="Stop" data-testid="stop-btn">
            <Square className="w-4 h-4 text-gray-300" />
          </button>
          <button
            onClick={togglePlay}
            className={`p-2 rounded ${isPlaying ? 'bg-green-500 text-white' : 'hover:bg-white/10 text-gray-300'}`}
            title="Play"
            data-testid="play-btn"
          >
            {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          </button>
          <button
            onClick={toggleRecord}
            className={`p-2 rounded ${isRecording ? 'bg-red-500 text-white animate-pulse' : 'hover:bg-white/10 text-gray-300'}`}
            title="Record"
            data-testid="record-btn"
          >
            <Circle className={`w-4 h-4 ${isRecording ? 'fill-current' : ''}`} />
          </button>
        </div>
        
        {/* Undo/Redo */}
        <div className="flex items-center gap-1 bg-[#0a0a0a] rounded-lg p-1 border border-[#333]">
          <button 
            onClick={undo} 
            className={`p-2 rounded ${historyIndex > 0 ? 'hover:bg-white/10 text-gray-300' : 'text-gray-600 cursor-not-allowed'}`}
            title="Undo (Ctrl+Z)"
            data-testid="undo-btn"
            disabled={historyIndex <= 0}
          >
            <Undo2 className="w-4 h-4" />
          </button>
          <button 
            onClick={redo}
            className={`p-2 rounded ${historyIndex < history.length - 1 ? 'hover:bg-white/10 text-gray-300' : 'text-gray-600 cursor-not-allowed'}`}
            title="Redo (Ctrl+Y)"
            data-testid="redo-btn"
            disabled={historyIndex >= history.length - 1}
          >
            <Redo2 className="w-4 h-4" />
          </button>
        </div>
        
        {/* Position Display */}
        <div className="bg-[#0a0a0a] px-4 py-2 rounded-lg border border-[#333] font-mono text-green-400 text-sm min-w-[80px] text-center">
          {position}
        </div>
        
        <div className="w-px h-8 bg-[#333]" />
        
        {/* Tempo & Time Sig */}
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-gray-500" />
          <input
            type="number"
            value={tempo}
            onChange={(e) => setTempo(parseInt(e.target.value) || 120)}
            className="w-16 bg-[#0a0a0a] text-white text-center rounded-lg px-2 py-1.5 border border-[#333] font-mono"
            min="40" max="240"
            data-testid="tempo-input"
          />
          <span className="text-gray-500 text-xs">BPM</span>
        </div>
        
        <select
          value={timeSignature}
          onChange={(e) => setTimeSignature(e.target.value)}
          className="bg-[#0a0a0a] text-white rounded-lg px-3 py-1.5 border border-[#333]"
          data-testid="time-sig"
        >
          <option value="4/4">4/4</option>
          <option value="3/4">3/4</option>
          <option value="6/8">6/8</option>
        </select>
        
        <div className="w-px h-8 bg-[#333]" />
        
        {/* Metronome & Loop */}
        <button
          onClick={() => setMetronomeOn(!metronomeOn)}
          className={`p-2 rounded-lg border ${metronomeOn ? 'bg-blue-500 border-blue-400 text-white' : 'border-[#333] text-gray-400 hover:bg-white/10'}`}
          title="Metronome"
          data-testid="metronome-btn"
        >
          <Activity className="w-4 h-4" />
        </button>
        
        {/* Loop Controls */}
        <div className="flex items-center gap-1">
          <button
            onClick={toggleLoop}
            className={`p-2 rounded-lg border ${loopOn ? 'bg-yellow-500 border-yellow-400 text-black' : 'border-[#333] text-gray-400 hover:bg-white/10'}`}
            title={`Loop ${loopOn ? 'ON' : 'OFF'} (${loopStart}-${loopEnd})`}
            data-testid="loop-btn"
          >
            <Repeat className="w-4 h-4" />
          </button>
          {loopOn && (
            <div className="flex items-center gap-1 bg-[#0a0a0a] rounded px-2 py-1 border border-yellow-500/50">
              <input
                type="number"
                value={loopStart}
                onChange={(e) => setLoopRegion(Math.max(1, parseInt(e.target.value) || 1), loopEnd)}
                className="w-10 bg-transparent text-yellow-400 text-center text-xs"
                min="1"
                max={loopEnd - 1}
              />
              <span className="text-yellow-400 text-xs">-</span>
              <input
                type="number"
                value={loopEnd}
                onChange={(e) => setLoopRegion(loopStart, Math.min(measures, parseInt(e.target.value) || measures))}
                className="w-10 bg-transparent text-yellow-400 text-center text-xs"
                min={loopStart + 1}
                max={measures}
              />
            </div>
          )}
        </div>
        
        {/* Floating Transport Button */}
        <button
          onClick={() => setShowFloatingTransport(!showFloatingTransport)}
          className={`p-2 rounded-lg border ${showFloatingTransport ? 'bg-purple-500 border-purple-400 text-white' : 'border-[#333] text-gray-400 hover:bg-white/10'}`}
          title="Floating Transport Window"
          data-testid="floating-transport-btn"
        >
          <Layers className="w-4 h-4" />
        </button>
        
        {/* MIDI Recording Mode */}
        <button
          onClick={() => setShowMidiPanel(!showMidiPanel)}
          className={`p-2 rounded-lg border ${midiRecordingMode ? 'bg-purple-500 border-purple-400 text-white' : 'border-[#333] text-gray-400 hover:bg-white/10'}`}
          title="MIDI Recording Mode"
          data-testid="midi-mode-btn"
        >
          <Keyboard className="w-4 h-4" />
        </button>
        
        <div className="flex-1" />
        
        {/* View Toggle */}
        <div className="flex items-center bg-[#0a0a0a] rounded-lg p-1 border border-[#333]">
          <button
            onClick={() => setViewMode('arrangement')}
            className={`px-3 py-1.5 rounded text-xs font-medium ${viewMode === 'arrangement' ? 'bg-blue-500 text-white' : 'text-gray-400 hover:text-white'}`}
            data-testid="view-arrangement"
          >
            <Grid className="w-4 h-4 inline mr-1" />
            Arrange
          </button>
          <button
            onClick={() => setViewMode('pianoroll')}
            className={`px-3 py-1.5 rounded text-xs font-medium ${viewMode === 'pianoroll' ? 'bg-blue-500 text-white' : 'text-gray-400 hover:text-white'}`}
            data-testid="view-pianoroll"
          >
            <Piano className="w-4 h-4 inline mr-1" />
            Piano Roll
          </button>
        </div>
        
        {/* Zoom */}
        <div className="flex items-center gap-1 bg-[#0a0a0a] rounded-lg p-1 border border-[#333]">
          <button onClick={() => setZoom(Math.max(0.01, zoom * 0.5))} className="p-1.5 hover:bg-white/10 rounded" data-testid="zoom-out">
            <ZoomOut className="w-4 h-4 text-gray-400" />
          </button>
          <span className="text-xs text-gray-400 w-12 text-center">{Math.round(zoom * 100)}%</span>
          <button onClick={() => setZoom(Math.min(4, zoom * 2))} className="p-1.5 hover:bg-white/10 rounded" data-testid="zoom-in">
            <ZoomIn className="w-4 h-4 text-gray-400" />
          </button>
        </div>
        
        <div className="w-px h-8 bg-[#333]" />
        
        {/* Actions */}
        <button onClick={() => setShowPackageBuilder(true)} className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 rounded-lg text-xs font-medium" data-testid="package-builder">
          <Package className="w-4 h-4 inline mr-1" /> Packages
        </button>
        <Link to="/admin/kit-samples" className="px-3 py-1.5 bg-teal-600 hover:bg-teal-700 rounded-lg text-xs font-medium flex items-center" data-testid="kit-samples-link">
          <FolderPlus className="w-4 h-4 inline mr-1" /> Kit Samples
        </Link>
        <button onClick={() => setShowAutoGenerate(true)} className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 rounded-lg text-xs font-medium" data-testid="auto-create">
          <Wand2 className="w-4 h-4 inline mr-1" /> Auto
        </button>
        <button onClick={() => setShowEffectsPanel(!showEffectsPanel)} className={`px-3 py-1.5 rounded-lg text-xs font-medium ${showEffectsPanel ? 'bg-green-600' : 'bg-[#333] hover:bg-[#444]'}`} data-testid="effects-toggle">
          <Sliders className="w-4 h-4 inline mr-1" /> FX
        </button>
        <button onClick={() => setShowMixer(!showMixer)} className={`px-3 py-1.5 rounded-lg text-xs font-medium ${showMixer ? 'bg-orange-600' : 'bg-[#333] hover:bg-[#444]'}`} data-testid="mixer-toggle">
          <SlidersHorizontal className="w-4 h-4 inline mr-1" /> Mixer
        </button>
        
        <div className="w-px h-8 bg-[#333]" />
        
        {/* Project Actions */}
        <button onClick={newProject} className="px-3 py-1.5 bg-[#333] hover:bg-[#444] rounded-lg text-xs font-medium" title="New Project" data-testid="new-project-btn">
          <FilePlus className="w-4 h-4 inline mr-1" /> New
        </button>
        <button onClick={() => { loadProjectsList(); setShowProjectsModal(true); }} className="px-3 py-1.5 bg-[#333] hover:bg-[#444] rounded-lg text-xs font-medium" title="Open Project" data-testid="open-project-btn">
          <FolderOpen className="w-4 h-4 inline mr-1" /> Open
        </button>
        <button onClick={saveProject} disabled={isSaving} className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-lg text-xs font-medium" data-testid="save-btn">
          <Save className="w-4 h-4 inline mr-1" /> {isSaving ? 'Saving...' : 'Save'}
        </button>
      </header>
      
      {/* ================================================================ */}
      {/* MAIN CONTENT */}
      {/* ================================================================ */}
      <div className="flex-1 flex overflow-hidden">
        {/* Track List */}
        <div className="w-52 bg-[#141414] border-r border-[#333] flex flex-col shrink-0">
          <div className="p-2 border-b border-[#333]">
            <button onClick={() => addTrack('piano')} className="w-full py-2 bg-[#252525] hover:bg-[#333] rounded-lg text-xs font-medium flex items-center justify-center gap-2" data-testid="add-track">
              <Plus className="w-4 h-4" /> Add Track
            </button>
          </div>
          
          <div className="flex-1 overflow-y-auto">
            {tracks.length === 0 && (
              <div className="p-4 text-center text-gray-500 text-xs">No tracks. Click "Add Track".</div>
            )}
            
            {tracks.map((track, idx) => (
              <TrackListItem
                key={track.id}
                track={track}
                index={idx}
                isSelected={selectedTrackId === track.id}
                onSelect={() => setSelectedTrackId(track.id)}
                onMute={() => toggleMute(track.id)}
                onSolo={() => toggleSolo(track.id)}
                onRecordArm={() => toggleRecordArm(track.id)}
                onDelete={() => deleteTrack(track.id)}
                onChangeInstrument={(instId) => changeTrackInstrument(track.id, instId)}
                onTestSound={async () => {
                  // Direct test - play the sample using HTML5 Audio
                  try {
                    await Tone.start();
                    
                    // Handle kit/package tracks
                    if (track.isPackage && track.packageId) {
                      // First try from state
                      let pkgSamples = kitSamples.filter(s => s.kit_id === track.packageId);
                      
                      // If no samples in state, fetch directly from API
                      if (pkgSamples.length === 0) {
                        console.log(`No samples in state for ${track.packageId}, fetching from API...`);
                        const token = localStorage.getItem('auth_token');
                        const res = await fetch(`${API}/api/kit-samples/by-kit/${track.packageId}`, {
                          headers: { 'Authorization': `Bearer ${token}` }
                        });
                        if (res.ok) {
                          const data = await res.json();
                          pkgSamples = data.samples || [];
                          console.log(`Fetched ${pkgSamples.length} samples from API`);
                        }
                      }
                      
                      if (pkgSamples.length > 0) {
                        const sample = pkgSamples[0];
                        const sampleUrl = `${API}${sample.url}`;
                        console.log(`Testing kit sample: ${sampleUrl}`);
                        const audio = new Audio(sampleUrl);
                        audio.volume = 0.7;
                        await audio.play();
                        toast.success(`Playing: ${sample.filename}`);
                      } else {
                        toast.error(`No samples found for kit: ${track.name}. Upload samples in Kit Sample Manager.`);
                      }
                      return;
                    }
                    
                    const audioSource = samplersRef.current[track.instrumentId];
                    
                    if (audioSource && audioSource.loaded) {
                      // Try Tone.js Player
                      audioSource.stop();
                      audioSource.start();
                      toast.success(`Playing: ${track.name}`);
                    } else {
                      // Fallback to HTML5 Audio
                      const sampleId = track.instrumentId.replace('user:', '');
                      const sample = userSamples[sampleId];
                      if (sample?.url) {
                        const fullUrl = sample.url.startsWith('http') ? sample.url : `${API}${sample.url}`;
                        const audio = new Audio(fullUrl);
                        audio.volume = 0.7;
                        audio.play();
                        toast.success(`Playing (direct): ${track.name}`);
                      } else {
                        toast.error(`Sample not found: ${track.name}`);
                      }
                    }
                  } catch (e) {
                    console.error('Test sound error:', e);
                    toast.error(`Error: ${e.message}`);
                  }
                }}
                userSamples={userSamples}
                instrumentPackages={instrumentPackages}
                loadingInstrument={loadingInstrument}
                isAuthenticated={isAuthenticated}
                authLoading={authLoading}
              />
            ))}
          </div>
        </div>
        
        {/* Main View Area */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {viewMode === 'arrangement' ? (
            <ArrangementView
              tracks={tracks}
              measures={measures}
              timeSignature={timeSignature}
              zoom={zoom}
              selectedTrackId={selectedTrackId}
              onSelectTrack={setSelectedTrackId}
              loopOn={loopOn}
              loopStart={loopStart}
              loopEnd={loopEnd}
              setLoopStart={setLoopStart}
              setLoopEnd={setLoopEnd}
              setLoopOn={setLoopOn}
            />
          ) : (
            <PianoRollView
              track={selectedTrack}
              measures={measures}
              timeSignature={timeSignature}
              zoom={zoom}
              onNotesChange={(notes) => selectedTrack && updateTrackNotes(selectedTrack.id, notes)}
              sampler={selectedTrack ? samplersRef.current[selectedTrack.instrumentId] : null}
              kitSamples={kitSamples}
              instrumentPackages={instrumentPackages}
              samplersRef={samplersRef}
              API={API}
              metronomeOn={metronomeOn}
              setMetronomeOn={setMetronomeOn}
              loopOn={loopOn}
              loopStart={loopStart}
              loopEnd={loopEnd}
              setLoopStart={setLoopStart}
              setLoopEnd={setLoopEnd}
              setLoopOn={setLoopOn}
            />
          )}
        </div>
        
        {/* Effects Panel */}
        <AnimatePresence>
          {showEffectsPanel && selectedTrack && (
            <motion.div
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: 300, opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              className="bg-[#141414] border-l border-[#333] overflow-hidden shrink-0"
            >
              <EffectsPanel
                track={selectedTrack}
                projectTempo={tempo}
                onUpdateEffect={(effect, value) => updateTrackEffect(selectedTrack.id, effect, value)}
                onUpdateSampleBpm={(bpm) => updateSampleBpm(selectedTrack.id, bpm)}
                onToggleSampleSync={() => toggleSampleSync(selectedTrack.id)}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      
      {/* ================================================================ */}
      {/* MIXER SECTION */}
      {/* ================================================================ */}
      <AnimatePresence>
        {showMixer && (
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: 180 }}
            exit={{ height: 0 }}
            className="bg-gradient-to-t from-[#0a0a0a] to-[#141414] border-t border-[#333] overflow-hidden"
          >
            <MixerSection
              tracks={tracks}
              onVolumeChange={updateTrackVolume}
              onPanChange={updateTrackPan}
              onMute={toggleMute}
              onSolo={toggleSolo}
              selectedTrackId={selectedTrackId}
              onSelectTrack={setSelectedTrackId}
            />
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* ================================================================ */}
      {/* MIDI RECORDING PANEL */}
      {/* ================================================================ */}
      <AnimatePresence>
        {showMidiPanel && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="bg-gradient-to-r from-purple-900/30 to-[#141414] border-t border-purple-500/30 overflow-hidden"
          >
            <div className="p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Keyboard className="w-5 h-5 text-purple-400" />
                  <span className="font-semibold">MIDI Recording Mode</span>
                  <button
                    onClick={() => setMidiRecordingMode(!midiRecordingMode)}
                    className={`px-3 py-1 rounded-full text-xs font-medium ${midiRecordingMode ? 'bg-purple-500 text-white' : 'bg-gray-700 text-gray-300'}`}
                  >
                    {midiRecordingMode ? 'ENABLED' : 'DISABLED'}
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-400">Quantize:</span>
                  <button
                    onClick={() => setMidiQuantize(!midiQuantize)}
                    className={`px-2 py-1 rounded text-xs ${midiQuantize ? 'bg-green-500/20 text-green-400 border border-green-500/50' : 'bg-gray-700 text-gray-400'}`}
                  >
                    {midiQuantize ? 'Grid' : 'Free'}
                  </button>
                  <button onClick={() => setShowMidiPanel(false)} className="p-1 hover:bg-white/10 rounded">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              {/* Recording Controls */}
              <div className="flex items-center gap-4 mb-4">
                <button
                  onClick={isRecording ? stopMidiRecording : startMidiRecording}
                  disabled={!midiRecordingMode || !selectedTrack}
                  className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 ${
                    isRecording 
                      ? 'bg-red-500 text-white animate-pulse' 
                      : midiRecordingMode && selectedTrack
                        ? 'bg-purple-500 hover:bg-purple-600 text-white'
                        : 'bg-gray-700 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {isRecording ? (
                    <><Square className="w-4 h-4" /> Stop Recording</>
                  ) : (
                    <><Circle className="w-4 h-4 fill-current" /> Start Recording</>
                  )}
                </button>
                {!selectedTrack && midiRecordingMode && (
                  <span className="text-xs text-yellow-400">Select a track to record</span>
                )}
                {selectedTrack && !selectedTrack.isPackage && midiRecordingMode && (
                  <span className="text-xs text-green-400">Melodic recording mode - Use keyboard A-L for white keys, W-P for black keys</span>
                )}
              </div>
              
              {/* Keyboard Map Visual for Drum Kits */}
              {midiRecordingMode && selectedTrack?.isPackage && (
                <div className="bg-[#0a0a0a] rounded-lg p-3">
                  <div className="text-xs text-gray-400 mb-2">Keyboard Mapping for: {selectedTrack.name}</div>
                  <div className="space-y-1">
                    {/* Top row Q-P */}
                    <div className="flex gap-1">
                      {['Q','W','E','R','T','Y','U','I','O','P'].map((key, i) => {
                        const pkg = instrumentPackages.find(p => p.id === selectedTrack.packageId);
                        const subInst = pkg?.subInstruments?.[i + 16];
                        return (
                          <div 
                            key={key}
                            className={`w-10 h-10 rounded flex flex-col items-center justify-center text-xs border ${
                              activeKeys.has(`Key${key}`) ? 'bg-purple-500 border-purple-400' : 'bg-[#1a1a1a] border-[#333]'
                            }`}
                          >
                            <span className="text-[10px] text-gray-500">{key}</span>
                            {subInst && <span className="text-[8px] text-purple-300 truncate max-w-full">{subInst.name?.slice(0,4)}</span>}
                          </div>
                        );
                      })}
                    </div>
                    {/* Middle row A-L */}
                    <div className="flex gap-1 ml-3">
                      {['A','S','D','F','G','H','J','K','L'].map((key, i) => {
                        const pkg = instrumentPackages.find(p => p.id === selectedTrack.packageId);
                        const subInst = pkg?.subInstruments?.[i + 7];
                        return (
                          <div 
                            key={key}
                            className={`w-10 h-10 rounded flex flex-col items-center justify-center text-xs border ${
                              activeKeys.has(`Key${key}`) ? 'bg-purple-500 border-purple-400' : 'bg-[#1a1a1a] border-[#333]'
                            }`}
                          >
                            <span className="text-[10px] text-gray-500">{key}</span>
                            {subInst && <span className="text-[8px] text-purple-300 truncate max-w-full">{subInst.name?.slice(0,4)}</span>}
                          </div>
                        );
                      })}
                    </div>
                    {/* Bottom row Z-M */}
                    <div className="flex gap-1 ml-6">
                      {['Z','X','C','V','B','N','M'].map((key, i) => {
                        const pkg = instrumentPackages.find(p => p.id === selectedTrack.packageId);
                        const subInst = pkg?.subInstruments?.[i];
                        return (
                          <div 
                            key={key}
                            className={`w-10 h-10 rounded flex flex-col items-center justify-center text-xs border ${
                              activeKeys.has(`Key${key}`) ? 'bg-purple-500 border-purple-400' : 'bg-[#1a1a1a] border-[#333]'
                            }`}
                          >
                            <span className="text-[10px] text-gray-500">{key}</span>
                            {subInst && <span className="text-[8px] text-purple-300 truncate max-w-full">{subInst.name?.slice(0,4)}</span>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  
                  {/* MIDI Device Status */}
                  <div className="mt-3 pt-3 border-t border-[#333] flex items-center justify-between">
                    <div className="text-xs text-gray-500">
                      MIDI Devices: {availableMidiDevices.length > 0 ? availableMidiDevices.map(d => d.name).join(', ') : 'None detected'}
                    </div>
                    <div className="text-xs text-gray-500">
                      Press keys to play samples
                    </div>
                  </div>
                </div>
              )}
              
              {/* Visual Piano Keyboard for Melodic Instruments */}
              {midiRecordingMode && selectedTrack && !selectedTrack.isPackage && (
                <div className="bg-[#0a0a0a] rounded-lg p-3">
                  <div className="text-xs text-gray-400 mb-2">Piano Keyboard for: {selectedTrack.name}</div>
                  <div className="flex justify-center">
                    {/* Render 2 octaves of piano keys */}
                    {[3, 4].map(octave => (
                      <div key={octave} className="flex relative">
                        {/* White keys */}
                        {['C', 'D', 'E', 'F', 'G', 'A', 'B'].map((note, idx) => {
                          const noteName = `${note}${octave}`;
                          const isActive = activeKeys.has(PIANO_KEYBOARD_MAP ? 
                            Object.keys(PIANO_KEYBOARD_MAP).find(k => PIANO_KEYBOARD_MAP[k] === noteName) : false
                          );
                          return (
                            <div 
                              key={noteName}
                              className={`w-8 h-20 border border-gray-600 rounded-b flex items-end justify-center pb-1 cursor-pointer transition-colors ${
                                isActive ? 'bg-purple-400 shadow-lg shadow-purple-400/50' : 'bg-white hover:bg-gray-100'
                              }`}
                              onMouseDown={async () => {
                                const sampler = samplersRef.current[selectedTrack.instrumentId];
                                if (sampler) {
                                  await Tone.start();
                                  sampler.triggerAttack(noteName);
                                }
                              }}
                              onMouseUp={() => {
                                const sampler = samplersRef.current[selectedTrack.instrumentId];
                                if (sampler) sampler.triggerRelease(noteName);
                              }}
                              onMouseLeave={() => {
                                const sampler = samplersRef.current[selectedTrack.instrumentId];
                                if (sampler) sampler.triggerRelease(noteName);
                              }}
                            >
                              <span className={`text-[8px] ${isActive ? 'text-white' : 'text-gray-500'}`}>{noteName}</span>
                            </div>
                          );
                        })}
                        {/* Black keys - positioned absolutely */}
                        {[0, 1, 3, 4, 5].map((offset, i) => {
                          const blackNotes = ['C#', 'D#', 'F#', 'G#', 'A#'];
                          const noteName = `${blackNotes[i]}${octave}`;
                          const leftPositions = [20, 52, 116, 148, 180]; // px positions
                          return (
                            <div 
                              key={noteName}
                              className={`absolute w-5 h-12 rounded-b cursor-pointer transition-colors ${
                                activeKeys.has(noteName) ? 'bg-purple-500 shadow-lg shadow-purple-500/50' : 'bg-gray-900 hover:bg-gray-700'
                              }`}
                              style={{ left: leftPositions[i] + (octave - 3) * 224, top: 0, zIndex: 10 }}
                              onMouseDown={async () => {
                                const sampler = samplersRef.current[selectedTrack.instrumentId];
                                if (sampler) {
                                  await Tone.start();
                                  sampler.triggerAttack(noteName);
                                }
                              }}
                              onMouseUp={() => {
                                const sampler = samplersRef.current[selectedTrack.instrumentId];
                                if (sampler) sampler.triggerRelease(noteName);
                              }}
                              onMouseLeave={() => {
                                const sampler = samplersRef.current[selectedTrack.instrumentId];
                                if (sampler) sampler.triggerRelease(noteName);
                              }}
                            />
                          );
                        })}
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 text-xs text-gray-500 text-center">
                    Computer Keyboard: A-L = white keys (C-B), W-P = black keys | Or use your MIDI controller
                  </div>
                  {/* MIDI Device Status */}
                  <div className="mt-2 pt-2 border-t border-[#333] flex items-center justify-between">
                    <div className="text-xs text-gray-500">
                      MIDI Devices: {availableMidiDevices.length > 0 ? availableMidiDevices.map(d => d.name).join(', ') : 'None detected'}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Modals */}
      <AnimatePresence>
        {showAutoGenerate && (
          <Modal onClose={() => setShowAutoGenerate(false)} title="Auto-Generate Pattern">
            <p className="text-gray-400 text-sm mb-4">Generate notes for all tracks. Settings preserved.</p>
            <div className="space-y-2">
              {['samba', 'bossa', 'baiao'].map(style => (
                <button key={style} onClick={() => autoGenerateScore(style)} className="w-full py-3 bg-[#252525] hover:bg-[#333] rounded-lg text-left px-4 capitalize font-medium">
                  {style === 'bossa' ? 'Bossa Nova' : style.charAt(0).toUpperCase() + style.slice(1)}
                </button>
              ))}
            </div>
          </Modal>
        )}
      </AnimatePresence>
      
      <ScoreViewModal isOpen={showScoreView} onClose={() => setShowScoreView(false)} tracks={tracks} timeSignature={timeSignature} tempo={tempo} />
      
      {/* Instrument Package Builder Modal */}
      <AnimatePresence>
        {showPackageBuilder && (
          <InstrumentPackageBuilder
            isOpen={showPackageBuilder}
            onClose={() => setShowPackageBuilder(false)}
            userSamples={userSamples}
            packages={instrumentPackages}
            onSavePackage={async (pkg) => {
              try {
                const token = localStorage.getItem('auth_token');
                const res = await fetch(`${API}/api/instrument-packages`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                  },
                  body: JSON.stringify(pkg)
                });
                if (res.ok) {
                  const saved = await res.json();
                  setInstrumentPackages(prev => [...prev.filter(p => p.id !== saved.id), saved]);
                  toast.success(`Saved package: ${pkg.name}`);
                } else {
                  toast.error('Failed to save package');
                }
              } catch (e) {
                console.error('Save package error:', e);
                toast.error('Failed to save package');
              }
            }}
            onDeletePackage={async (pkgId) => {
              try {
                const token = localStorage.getItem('auth_token');
                await fetch(`${API}/api/instrument-packages/${pkgId}`, {
                  method: 'DELETE',
                  headers: { 'Authorization': `Bearer ${token}` }
                });
                setInstrumentPackages(prev => prev.filter(p => p.id !== pkgId));
                toast.success('Package deleted');
              } catch (e) {
                toast.error('Failed to delete package');
              }
            }}
          />
        )}
      </AnimatePresence>
      
      {/* Projects Modal */}
      <AnimatePresence>
        {showProjectsModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50"
            onClick={() => setShowProjectsModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-[#1a1a1a] rounded-xl border border-[#333] w-[600px] max-h-[80vh] overflow-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-[#333] flex items-center justify-between">
                <h2 className="text-xl font-bold">My Projects</h2>
                <button onClick={() => setShowProjectsModal(false)} className="p-1 hover:bg-white/10 rounded">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="p-6 max-h-[60vh] overflow-y-auto">
                {savedProjects.length === 0 ? (
                  <div className="text-center text-gray-500 py-8">
                    <FolderOpen className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No saved projects yet</p>
                    <p className="text-sm mt-2">Create a project and click Save to see it here</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {savedProjects.map((project) => (
                      <div 
                        key={project.id}
                        className={`p-4 rounded-lg border ${projectId === project.id ? 'bg-blue-900/30 border-blue-500' : 'bg-[#252525] border-[#333] hover:border-[#444]'} cursor-pointer`}
                        onClick={() => loadProject(project.id)}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="font-medium">{project.name}</h3>
                            <p className="text-xs text-gray-500 mt-1">
                              {project.tempo} BPM • {project.timeSignature} • {project.measures} measures
                            </p>
                            <p className="text-xs text-gray-600 mt-1">
                              Updated: {new Date(project.updatedAt).toLocaleDateString()} {new Date(project.updatedAt).toLocaleTimeString()}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            {projectId === project.id && (
                              <span className="text-xs bg-blue-500 text-white px-2 py-0.5 rounded">Current</span>
                            )}
                            <button 
                              onClick={(e) => { e.stopPropagation(); deleteProject(project.id); }}
                              className="p-2 text-gray-500 hover:text-red-400 hover:bg-red-500/20 rounded"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="px-6 py-4 border-t border-[#333] bg-[#141414] flex justify-end gap-2">
                <button onClick={() => setShowProjectsModal(false)} className="px-4 py-2 bg-[#333] hover:bg-[#444] rounded-lg text-sm">
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Pro Tools-style Floating Transport Window */}
      {showFloatingTransport && (
        <FloatingTransport
          isPlaying={isPlaying}
          isRecording={isRecording}
          position={position}
          tempo={tempo}
          timeSignature={timeSignature}
          metronomeOn={metronomeOn}
          loopOn={loopOn}
          loopStart={loopStart}
          loopEnd={loopEnd}
          onPlay={togglePlay}
          onStop={stopPlayback}
          onRecord={toggleRecord}
          onRewind={rewind}
          onFastForward={fastForward}
          onTempoChange={setTempo}
          onMetronomeToggle={() => setMetronomeOn(!metronomeOn)}
          onLoopToggle={toggleLoop}
          onLoopRegionChange={setLoopRegion}
          initialPosition={floatingTransportPos}
          onPositionChange={setFloatingTransportPos}
          minimized={floatingTransportSize.width < 300}
          onMinimizeToggle={() => setFloatingTransportSize(prev => 
            prev.width < 300 ? { width: 380, height: 90 } : { width: 280, height: 70 }
          )}
        />
      )}
    </div>
  );
};


// ============================================================================
// PIANO ROLL VIEW - Professional DAW-style editing
// ============================================================================
const PianoRollView = ({ track, measures, timeSignature, zoom, onZoomChange, onNotesChange, sampler, kitSamples = [], instrumentPackages = [], samplersRef, API, metronomeOn, setMetronomeOn, loopOn, loopStart, loopEnd, setLoopStart, setLoopEnd, setLoopOn }) => {
  const gridRef = useRef(null);
  const [notes, setNotes] = useState(track?.notes || []);
  const [tool, setTool] = useState('draw'); // 'draw', 'move', 'select', 'erase'
  const [verticalZoom, setVerticalZoom] = useState(1);
  
  // Note duration for drawing
  const [noteDuration, setNoteDuration] = useState('q'); // 'w', 'h', 'q', '8', '16', '32', '64'
  const noteDurations = [
    { value: 'w', label: 'Whole', beats: 4 },
    { value: 'h', label: 'Half', beats: 2 },
    { value: 'q', label: 'Quarter', beats: 1 },
    { value: '8', label: '8th', beats: 0.5 },
    { value: '16', label: '16th', beats: 0.25 },
    { value: '32', label: '32nd', beats: 0.125 },
    { value: '64', label: '64th', beats: 0.0625 },
  ];
  
  // Grid resolution for visual grid lines (independent of note size)
  // This controls how fine the grid lines appear - lower number = coarser grid
  const [gridResolution, setGridResolution] = useState(4); // Subdivisions to show: 1=quarter, 2=8th, 4=16th, 8=32nd, 16=64th
  const gridResolutions = [
    { value: 1, label: '1/4', desc: 'Quarter notes' },
    { value: 2, label: '1/8', desc: '8th notes' },
    { value: 4, label: '1/16', desc: '16th notes' },
    { value: 8, label: '1/32', desc: '32nd notes' },
    { value: 16, label: '1/64', desc: '64th notes' },
  ];
  
  // Snap to grid toggle
  const [snapEnabled, setSnapEnabled] = useState(true);
  
  // Selection state
  const [selectedNotes, setSelectedNotes] = useState(new Set()); // Set of note indices
  
  // Clipboard for copy/paste
  const [clipboard, setClipboard] = useState([]);
  
  // Context menu state
  const [contextMenu, setContextMenu] = useState(null); // { x, y } or null
  
  // Cursor state for note edge hover
  const [isHoveringEdge, setIsHoveringEdge] = useState(false);
  
  // Velocity/Volume lane state
  const [showVelocityLane, setShowVelocityLane] = useState(true);
  const [laneMode, setLaneMode] = useState('velocity'); // 'velocity' or 'volume'
  const [velocityLaneHeight] = useState(80); // Height of velocity lane in px
  const [editingVelocityNote, setEditingVelocityNote] = useState(null);
  
  // Loop region drag state
  const [isDraggingLoop, setIsDraggingLoop] = useState(false);
  const [loopDragType, setLoopDragType] = useState(null);
  const [loopDragStartX, setLoopDragStartX] = useState(null);
  const loopBarRef = useRef(null);
  
  // Drag state - use refs for values accessed during drag to avoid stale closures
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const dragOffsetRef = useRef({ x: 0, y: 0 });
  const [dragOffsetState, setDragOffsetState] = useState({ x: 0, y: 0 }); // For visual updates
  
  // Note resizing state
  const [isResizing, setIsResizing] = useState(false);
  const [resizingNoteIndex, setResizingNoteIndex] = useState(null);
  const [resizeEdge, setResizeEdge] = useState(null); // 'left' or 'right'
  const [resizeStartX, setResizeStartX] = useState(null);
  const [originalDurCells, setOriginalDurCells] = useState(null); // Store original duration
  const [originalStartCellX, setOriginalStartCellX] = useState(null); // Store original start position
  const resizingNoteIndexRef = useRef(null);
  const resizeEdgeRef = useRef(null);
  const resizeStartXRef = useRef(null);
  const originalDurCellsRef = useRef(null);
  const originalStartCellXRef = useRef(null);
  
  // Keep resize refs in sync
  useEffect(() => { resizingNoteIndexRef.current = resizingNoteIndex; }, [resizingNoteIndex]);
  useEffect(() => { resizeEdgeRef.current = resizeEdge; }, [resizeEdge]);
  useEffect(() => { resizeStartXRef.current = resizeStartX; }, [resizeStartX]);
  useEffect(() => { originalDurCellsRef.current = originalDurCells; }, [originalDurCells]);
  useEffect(() => { originalStartCellXRef.current = originalStartCellX; }, [originalStartCellX]);
  
  // Refs for current values during drag
  const notesRef = useRef(notes);
  const selectedNotesRef = useRef(selectedNotes);
  
  // Keep refs in sync
  useEffect(() => { notesRef.current = notes; }, [notes]);
  useEffect(() => { selectedNotesRef.current = selectedNotes; }, [selectedNotes]);
  
  // Marquee selection state
  const [isMarquee, setIsMarquee] = useState(false);
  const [marqueeStart, setMarqueeStart] = useState(null);
  const [marqueeEnd, setMarqueeEnd] = useState(null);
  const marqueeStartRef = useRef(null);
  const marqueeEndRef = useRef(null);
  
  // Keep marquee refs in sync
  useEffect(() => { marqueeStartRef.current = marqueeStart; }, [marqueeStart]);
  useEffect(() => { marqueeEndRef.current = marqueeEnd; }, [marqueeEnd]);
  
  // Base sizes - Grid resolution affects visual cell width
  // gridResolution: 1=1/4, 2=1/8, 4=1/16, 8=1/32, 16=1/64
  // When grid is coarser (1/4), each cell is visually wider
  // When grid is finer (1/64), each cell is visually narrower
  const baseCellWidth = 18;
  const baseCellHeight = 20;
  // Cell width scales with zoom AND grid resolution
  // At 1/4 grid (gridResolution=1), cells are 4x wider than at 1/16 grid (gridResolution=4)
  const gridScale = 4 / gridResolution; // 4 is the reference (1/16 grid)
  const cellWidth = baseCellWidth * zoom * gridScale;
  const cellHeight = Math.round(baseCellHeight * verticalZoom);
  const keyboardWidth = 120;
  const beatsPerMeasure = parseInt(timeSignature.split('/')[0]);
  const subdivisions = 16; // 16 subdivisions per beat allows 64th notes
  const totalCells = measures * beatsPerMeasure * subdivisions;
  
  // Track type
  const isKitTrack = track?.isPackage && track?.packageId;
  
  const getKitSamples = () => {
    if (!isKitTrack) return [];
    return kitSamples
      .filter(s => s.kit_id === track.packageId)
      .sort((a, b) => (a.filename || '').localeCompare(b.filename || ''));
  };
  
  const kitSamplesList = getKitSamples();
  const displayRows = isKitTrack ? kitSamplesList : ALL_NOTES;
  
  useEffect(() => { 
    if (track?.notes) setNotes(track.notes); 
    setSelectedNotes(new Set()); // Clear selection when track changes
  }, [track]);
  
  // Get note position info
  const getNotePosition = (note) => {
    let rowIndex;
    if (isKitTrack) {
      rowIndex = note.padIndex ?? kitSamplesList.findIndex(s => s.id === note.sampleId);
    } else {
      rowIndex = ALL_NOTES.indexOf(note.pitch.replace('/', ''));
    }
    const beat = note.measure * beatsPerMeasure + (note.beat || 0);
    const cellX = beat * subdivisions;
    // Map note duration to beats: w=4, h=2, q=1, 8=0.5, 16=0.25, 32=0.125, 64=0.0625
    const durationToBeats = {
      'w': 4, 'h': 2, 'q': 1, '8': 0.5, '16': 0.25, '32': 0.125, '64': 0.0625
    };
    const durBeats = durationToBeats[note.duration] || 1; // Default to quarter note
    return { rowIndex, cellX, durBeats };
  };
  
  // Play preview
  const playPreview = async (rowIndex) => {
    try {
      await Tone.start();
      if (isKitTrack && kitSamplesList[rowIndex]) {
        const sample = kitSamplesList[rowIndex];
        const audio = new Audio(`${API}${sample.url}`);
        audio.volume = 0.7;
        audio.play();
      } else if (sampler?.loaded) {
        const noteName = ALL_NOTES[rowIndex];
        if (typeof sampler.triggerAttackRelease === 'function') {
          sampler.triggerAttackRelease(noteName, '8n');
        }
      }
    } catch (e) { console.warn('Preview:', e); }
  };
  
  // Convert screen position to grid position
  const screenToGrid = (clientX, clientY) => {
    if (!gridRef.current) return null;
    const rect = gridRef.current.getBoundingClientRect();
    const x = clientX - rect.left + gridRef.current.scrollLeft - keyboardWidth;
    const y = clientY - rect.top + gridRef.current.scrollTop;
    const cellX = Math.floor(x / cellWidth);
    const cellY = Math.floor(y / cellHeight);
    return { cellX, cellY, x, y };
  };
  
  // Snap cell position to grid based on current grid resolution
  // gridResolution: 1=1/4, 2=1/8, 4=1/16, 8=1/32, 16=1/64
  // subdivisions=16, so snap interval = subdivisions / gridResolution
  const snapToGrid = (cellX) => {
    if (!snapEnabled) return cellX; // Bypass snap if disabled
    const snapInterval = subdivisions / gridResolution; // cells per grid line
    return Math.round(cellX / snapInterval) * snapInterval;
  };
  
  // Snap beat value to grid resolution
  const snapBeatToGrid = (beat) => {
    if (!snapEnabled) return beat; // Bypass snap if disabled
    // Grid resolution determines snap: 1=quarters, 2=8ths, 4=16ths, etc.
    const snapValue = 1 / gridResolution; // beat interval for snapping
    return Math.round(beat / snapValue) * snapValue;
  };
  
  // Find note at position
  const findNoteAtPosition = (cellX, cellY) => {
    return notes.findIndex(note => {
      const pos = getNotePosition(note);
      if (pos.rowIndex !== cellY) return false;
      const durCells = pos.durBeats * subdivisions;
      return cellX >= pos.cellX && cellX < pos.cellX + durCells;
    });
  };
  
  // Check if click is on the edge of a note (for resizing) - returns { index, edge: 'left' | 'right' } or null
  const findNoteEdgeAtPosition = (pixelX, cellY) => {
    const edgeThreshold = 12; // pixels from edge to trigger resize
    for (let i = 0; i < notes.length; i++) {
      const pos = getNotePosition(notes[i]);
      if (pos.rowIndex !== cellY) continue;
      const durCells = pos.durBeats * subdivisions;
      const noteLeftEdge = pos.cellX * cellWidth;
      const noteRightEdge = (pos.cellX + durCells) * cellWidth;
      
      // Check left edge first
      if (pixelX >= noteLeftEdge - edgeThreshold && pixelX <= noteLeftEdge + edgeThreshold) {
        return { index: i, edge: 'left' };
      }
      // Check right edge
      if (pixelX >= noteRightEdge - edgeThreshold && pixelX <= noteRightEdge + edgeThreshold) {
        return { index: i, edge: 'right' };
      }
    }
    return null;
  };
  
  // Handle grid mouse down
  const handleGridMouseDown = (e) => {
    if (!gridRef.current || !track) return;
    // Ignore right-clicks in mousedown - they're handled by contextMenu
    if (e.button === 2) return;
    
    const grid = screenToGrid(e.clientX, e.clientY);
    if (!grid || grid.x < 0) return;
    
    const { cellX, cellY, x: pixelX } = grid;
    if (cellY < 0 || cellY >= displayRows.length) return;
    
    const noteAtPos = findNoteAtPosition(cellX, cellY);
    
    if (tool === 'draw') {
      // Pencil tool: Check if clicking on note edge for resize
      const edgeResult = findNoteEdgeAtPosition(pixelX, cellY);
      
      if (edgeResult) {
        // Start resizing the note - store original duration and position
        const note = notes[edgeResult.index];
        const pos = getNotePosition(note);
        const durCells = pos.durBeats * subdivisions;
        
        setIsResizing(true);
        setResizingNoteIndex(edgeResult.index);
        setResizeEdge(edgeResult.edge);
        setResizeStartX(e.clientX);
        setOriginalDurCells(durCells);
        setOriginalStartCellX(pos.cellX);
      } else if (noteAtPos < 0) {
        // Single click on empty = add note with snap to grid
        const pitchId = isKitTrack ? `PAD:${kitSamplesList[cellY]?.id}` : ALL_NOTES[cellY];
        const snappedCellX = snapToGrid(cellX); // Snap to grid!
        const beat = snappedCellX / subdivisions;
        const measure = Math.floor(beat / beatsPerMeasure);
        const beatInMeasure = beat % beatsPerMeasure;
        
        const newNote = isKitTrack 
          ? { measure, beat: beatInMeasure, pitch: pitchId, padIndex: cellY, sampleId: kitSamplesList[cellY]?.id, duration: noteDuration, velocity: 0.8 }
          : { measure, beat: beatInMeasure, pitch: pitchId.replace(/(\d)/, '/$1'), duration: noteDuration, velocity: 0.8 };
        const newNotes = [...notes, newNote];
        setNotes(newNotes);
        onNotesChange(newNotes);
        playPreview(cellY);
      }
    } else if (tool === 'move') {
      // Hand tool - ONLY for moving already-selected notes
      if (noteAtPos >= 0 && selectedNotes.has(noteAtPos)) {
        // Start dragging selected notes
        setIsDragging(true);
        setDragStart({ x: e.clientX, y: e.clientY });
        setDragOffset({ x: 0, y: 0 });
      } else if (noteAtPos >= 0) {
        // Clicked on unselected note - select it and start drag
        setSelectedNotes(new Set([noteAtPos]));
        setIsDragging(true);
        setDragStart({ x: e.clientX, y: e.clientY });
        setDragOffset({ x: 0, y: 0 });
      }
    } else if (tool === 'select') {
      // Cross/Selection tool - for selecting notes
      if (noteAtPos >= 0) {
        // Clicked on a note - select it
        if (e.shiftKey) {
          // Shift+click: toggle selection
          setSelectedNotes(prev => {
            const next = new Set(prev);
            if (next.has(noteAtPos)) next.delete(noteAtPos);
            else next.add(noteAtPos);
            return next;
          });
        } else {
          // Regular click: select only this note
          setSelectedNotes(new Set([noteAtPos]));
        }
      } else {
        // Clicked on empty space - start marquee selection
        if (!e.shiftKey) setSelectedNotes(new Set());
        setIsMarquee(true);
        setMarqueeStart({ x: grid.x, y: grid.y });
        setMarqueeEnd({ x: grid.x, y: grid.y });
      }
    } else if (tool === 'erase') {
      if (noteAtPos >= 0) {
        const newNotes = notes.filter((_, i) => i !== noteAtPos);
        setNotes(newNotes);
        onNotesChange(newNotes);
      }
    }
  };
  
  // Handle double-click for pencil tool to erase notes
  const handleGridDoubleClick = (e) => {
    if (!gridRef.current || !track || tool !== 'draw') return;
    
    const grid = screenToGrid(e.clientX, e.clientY);
    if (!grid || grid.x < 0) return;
    
    const { cellX, cellY } = grid;
    if (cellY < 0 || cellY >= displayRows.length) return;
    
    const noteAtPos = findNoteAtPosition(cellX, cellY);
    
    // Double-click on existing note = erase it
    if (noteAtPos >= 0) {
      const newNotes = notes.filter((_, i) => i !== noteAtPos);
      setNotes(newNotes);
      onNotesChange(newNotes);
    }
  };
  
  // Handle grid hover for resize cursor
  const handleGridHover = (e) => {
    if (tool !== 'draw' || isDragging || isMarquee || isResizing) {
      setIsHoveringEdge(false);
      return;
    }
    
    const grid = screenToGrid(e.clientX, e.clientY);
    if (!grid || grid.x < 0) {
      setIsHoveringEdge(false);
      return;
    }
    
    const { cellY, x: pixelX } = grid;
    if (cellY < 0 || cellY >= displayRows.length) {
      setIsHoveringEdge(false);
      return;
    }
    
    const edgeResult = findNoteEdgeAtPosition(pixelX, cellY);
    setIsHoveringEdge(edgeResult !== null);
  };
  
  // Handle mouse move
  const handleMouseMove = useCallback((e) => {
    if (isDragging && dragStart) {
      const dx = e.clientX - dragStart.x;
      const dy = e.clientY - dragStart.y;
      // Only update if we've moved more than a small threshold
      if (Math.abs(dx) > 2 || Math.abs(dy) > 2) {
        dragOffsetRef.current = { x: dx, y: dy };
        setDragOffsetState({ x: dx, y: dy }); // Trigger visual update
      }
    } else if (isMarquee && marqueeStart) {
      const grid = screenToGrid(e.clientX, e.clientY);
      if (grid) setMarqueeEnd({ x: Math.max(0, grid.x), y: Math.max(0, grid.y) });
    } else if (isResizing && resizingNoteIndexRef.current !== null && resizeStartXRef.current !== null) {
      // Handle note resizing - calculate total delta from start
      const totalDx = e.clientX - resizeStartXRef.current;
      const cellDelta = Math.round(totalDx / cellWidth);
      
      const noteIndex = resizingNoteIndexRef.current;
      const storedOriginalDurCells = originalDurCellsRef.current;
      const storedOriginalStartCellX = originalStartCellXRef.current;
      const edge = resizeEdgeRef.current;
      
      if (storedOriginalDurCells !== null && storedOriginalStartCellX !== null) {
        const currentNote = notes[noteIndex];
        if (currentNote) {
          let newDurCells, newStartCellX;
          
          if (edge === 'right') {
            // Dragging right edge: change duration, keep start position
            // Snap the new end position to grid
            const newEndCellX = snapToGrid(storedOriginalStartCellX + storedOriginalDurCells + cellDelta);
            newDurCells = Math.max(snapToGrid(1), newEndCellX - storedOriginalStartCellX);
            newStartCellX = storedOriginalStartCellX;
          } else {
            // Dragging left edge: change start position and duration
            // Moving left (negative delta) = extend note earlier = increase duration
            // Moving right (positive delta) = shrink note = decrease duration
            // Snap the new start position to grid
            newStartCellX = snapToGrid(Math.max(0, storedOriginalStartCellX + cellDelta));
            const endCellX = storedOriginalStartCellX + storedOriginalDurCells;
            newDurCells = Math.max(snapToGrid(1), endCellX - newStartCellX);
          }
          
          const currentPos = getNotePosition(currentNote);
          const currentDurCells = currentPos.durBeats * subdivisions;
          
          // Check if anything changed
          if (newDurCells !== currentDurCells || (edge === 'left' && newStartCellX !== currentPos.cellX)) {
            const newDurBeats = newDurCells / subdivisions;
            
            // Map duration to note value
            let newDuration = 'q';
            if (newDurBeats <= 0.25) newDuration = '16';
            else if (newDurBeats <= 0.5) newDuration = '8';
            else if (newDurBeats <= 1) newDuration = 'q';
            else if (newDurBeats <= 2) newDuration = 'h';
            else newDuration = 'w';
            
            const newNotes = [...notes];
            const updatedNote = { ...currentNote, duration: newDuration };
            
            // If dragging left edge, also update the note's start position
            if (edge === 'left') {
              const newBeatTotal = newStartCellX / subdivisions;
              updatedNote.measure = Math.floor(newBeatTotal / beatsPerMeasure);
              updatedNote.beat = newBeatTotal % beatsPerMeasure;
            }
            
            newNotes[noteIndex] = updatedNote;
            setNotes(newNotes);
          }
        }
      }
    }
  }, [isDragging, dragStart, isMarquee, marqueeStart, isResizing, notes, cellWidth, subdivisions, gridResolution]);
  
  // Handle mouse up - use refs for current values
  const handleMouseUp = useCallback(() => {
    const offset = dragOffsetRef.current;
    const currentNotes = notesRef.current;
    const currentSelection = selectedNotesRef.current;
    const currentMarqueeStart = marqueeStartRef.current;
    const currentMarqueeEnd = marqueeEndRef.current;
    
    if (isDragging && currentSelection.size > 0) {
      // Finalize move with snap to grid
      const cellDeltaX = Math.round(offset.x / cellWidth);
      const cellDeltaY = Math.round(offset.y / cellHeight);
      
      if (cellDeltaX !== 0 || cellDeltaY !== 0) {
        const newNotes = currentNotes.map((note, i) => {
          if (!currentSelection.has(i)) return note;
          
          const updatedNote = { ...note };
          const currentBeat = note.measure * beatsPerMeasure + (note.beat || 0);
          const currentCellX = currentBeat * subdivisions;
          const newCellX = snapToGrid(currentCellX + cellDeltaX); // Snap to grid!
          const newBeatTotal = Math.max(0, newCellX / subdivisions);
          updatedNote.measure = Math.floor(newBeatTotal / beatsPerMeasure);
          updatedNote.beat = newBeatTotal % beatsPerMeasure;
          
          if (isKitTrack) {
            const currentRow = note.padIndex ?? kitSamplesList.findIndex(s => s.id === note.sampleId);
            const newRow = Math.max(0, Math.min(kitSamplesList.length - 1, currentRow + cellDeltaY));
            if (kitSamplesList[newRow]) {
              updatedNote.padIndex = newRow;
              updatedNote.sampleId = kitSamplesList[newRow].id;
              updatedNote.pitch = `PAD:${kitSamplesList[newRow].id}`;
            }
          } else {
            const currentRow = ALL_NOTES.indexOf(note.pitch.replace('/', ''));
            const newRow = Math.max(0, Math.min(ALL_NOTES.length - 1, currentRow + cellDeltaY));
            updatedNote.pitch = ALL_NOTES[newRow].replace(/(\d)/, '/$1');
          }
          
          return updatedNote;
        });
        setNotes(newNotes);
        onNotesChange(newNotes);
      }
    } else if (isMarquee && currentMarqueeStart && currentMarqueeEnd) {
      // Complete marquee selection
      const minX = Math.min(currentMarqueeStart.x, currentMarqueeEnd.x);
      const maxX = Math.max(currentMarqueeStart.x, currentMarqueeEnd.x);
      const minY = Math.min(currentMarqueeStart.y, currentMarqueeEnd.y);
      const maxY = Math.max(currentMarqueeStart.y, currentMarqueeEnd.y);
      
      const newSelection = new Set(currentSelection);
      currentNotes.forEach((note, i) => {
        const pos = getNotePosition(note);
        const noteLeft = pos.cellX * cellWidth;
        const noteRight = (pos.cellX + pos.durBeats * subdivisions) * cellWidth;
        const noteTop = pos.rowIndex * cellHeight;
        const noteBottom = (pos.rowIndex + 1) * cellHeight;
        
        if (noteRight > minX && noteLeft < maxX && noteBottom > minY && noteTop < maxY) {
          newSelection.add(i);
        }
      });
      setSelectedNotes(newSelection);
    } else if (isResizing) {
      // Finalize resize - already updated in real-time, just commit
      onNotesChange(notesRef.current);
    }
    
    setIsDragging(false);
    setDragStart(null);
    dragOffsetRef.current = { x: 0, y: 0 };
    setDragOffsetState({ x: 0, y: 0 });
    setIsMarquee(false);
    setMarqueeStart(null);
    setMarqueeEnd(null);
    setIsResizing(false);
    setResizingNoteIndex(null);
    setResizeEdge(null);
    setResizeStartX(null);
    setOriginalDurCells(null);
    setOriginalStartCellX(null);
  }, [isDragging, isMarquee, isResizing, cellWidth, cellHeight, beatsPerMeasure, subdivisions, isKitTrack, kitSamplesList, onNotesChange]);
  
  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Only handle if this component is focused (check if grid or its children have focus)
      if (!gridRef.current?.contains(document.activeElement) && document.activeElement !== document.body) return;
      
      // Delete/Backspace - erase selected notes
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedNotes.size > 0) {
        e.preventDefault();
        deleteSelection();
      }
      
      // Escape - deselect all and close context menu
      if (e.key === 'Escape') {
        setSelectedNotes(new Set());
        setContextMenu(null);
      }
      
      // Cmd/Ctrl+A - select all
      if ((e.metaKey || e.ctrlKey) && e.key === 'a') {
        e.preventDefault();
        setSelectedNotes(new Set(notes.map((_, i) => i)));
      }
      
      // Cmd/Ctrl+C - copy
      if ((e.metaKey || e.ctrlKey) && e.key === 'c' && selectedNotes.size > 0) {
        e.preventDefault();
        copySelection();
      }
      
      // Cmd/Ctrl+X - cut
      if ((e.metaKey || e.ctrlKey) && e.key === 'x' && selectedNotes.size > 0) {
        e.preventDefault();
        cutSelection();
      }
      
      // Cmd/Ctrl+V - paste
      if ((e.metaKey || e.ctrlKey) && e.key === 'v' && clipboard.length > 0) {
        e.preventDefault();
        pasteClipboard();
      }
      
      // Cmd/Ctrl+D - duplicate
      if ((e.metaKey || e.ctrlKey) && e.key === 'd' && selectedNotes.size > 0) {
        e.preventDefault();
        duplicateSelection();
      }
      
      // Grid resolution shortcuts (Cmd/Ctrl + 1-5)
      if ((e.metaKey || e.ctrlKey) && ['1','2','3','4','5'].includes(e.key)) {
        e.preventDefault();
        const resolutions = [1, 2, 4, 8, 16]; // 1/4, 1/8, 1/16, 1/32, 1/64
        const idx = parseInt(e.key) - 1;
        if (idx >= 0 && idx < resolutions.length) {
          setGridResolution(resolutions[idx]);
        }
      }
      
      // Tool shortcuts (only when not using modifier keys)
      if (!e.metaKey && !e.ctrlKey) {
        if (e.key === 'p') { setTool('draw'); }
        if (e.key === 'h') { setTool('move'); }
        if (e.key === 's') { setTool('select'); }
        if (e.key === 'e') { setTool('erase'); }
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedNotes, notes, clipboard, onNotesChange]);
  
  // Option/Alt + Scroll wheel for zoom (like Ableton/Pro Tools)
  const handleWheel = useCallback((e) => {
    // Option/Alt + scroll = horizontal zoom
    if (e.altKey) {
      e.preventDefault();
      const delta = e.deltaY > 0 ? 0.9 : 1.1; // Scroll down = zoom out, scroll up = zoom in
      const newZoom = Math.max(0.01, Math.min(4, zoom * delta));
      if (onZoomChange) onZoomChange(newZoom);
    }
    // Shift + Option/Alt + scroll = vertical zoom
    else if (e.shiftKey && e.altKey) {
      e.preventDefault();
      const delta = e.deltaY > 0 ? 0.9 : 1.1;
      setVerticalZoom(prev => Math.max(0.1, Math.min(4, prev * delta)));
    }
  }, [zoom, onZoomChange]);
  
  // Attach wheel event to grid
  useEffect(() => {
    const grid = gridRef.current;
    if (grid) {
      grid.addEventListener('wheel', handleWheel, { passive: false });
      return () => grid.removeEventListener('wheel', handleWheel);
    }
  }, [handleWheel]);
  
  // Attach mouse events for dragging/marquee/resizing
  useEffect(() => {
    if (isDragging || isMarquee || isResizing) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, isMarquee, isResizing, handleMouseMove, handleMouseUp]);
  
  const isBlackKey = (note) => note.includes?.('#');
  
  // Delete selected notes
  const deleteSelection = () => {
    if (selectedNotes.size === 0) return;
    const newNotes = notes.filter((_, i) => !selectedNotes.has(i));
    setNotes(newNotes);
    onNotesChange(newNotes);
    setSelectedNotes(new Set());
    setContextMenu(null);
  };
  
  // Copy selected notes to clipboard
  const copySelection = () => {
    if (selectedNotes.size === 0) return;
    
    // Get selected notes and find the earliest beat position
    const selectedNotesList = notes.filter((_, i) => selectedNotes.has(i));
    if (selectedNotesList.length === 0) return;
    
    // Find the minimum beat position to use as reference
    const minBeat = Math.min(...selectedNotesList.map(n => n.measure * beatsPerMeasure + (n.beat || 0)));
    
    // Store notes with relative positions
    const clipboardNotes = selectedNotesList.map(note => ({
      ...note,
      relativeBeat: (note.measure * beatsPerMeasure + (note.beat || 0)) - minBeat
    }));
    
    setClipboard(clipboardNotes);
    setContextMenu(null);
  };
  
  // Cut selected notes (copy + delete)
  const cutSelection = () => {
    copySelection();
    deleteSelection();
  };
  
  // Paste notes from clipboard at current position (offset by 1 beat from last note)
  const pasteClipboard = () => {
    if (clipboard.length === 0) return;
    
    // Find the latest beat position in existing notes
    let pasteStartBeat = 0;
    if (notes.length > 0) {
      const maxBeat = Math.max(...notes.map(n => n.measure * beatsPerMeasure + (n.beat || 0)));
      pasteStartBeat = maxBeat + 1; // Paste 1 beat after the last note
    }
    
    // Create new notes from clipboard
    const newNotesFromClipboard = clipboard.map(clipNote => {
      const totalBeat = pasteStartBeat + clipNote.relativeBeat;
      const measure = Math.floor(totalBeat / beatsPerMeasure);
      const beat = totalBeat % beatsPerMeasure;
      
      const newNote = { ...clipNote, measure, beat };
      delete newNote.relativeBeat;
      return newNote;
    });
    
    const updatedNotes = [...notes, ...newNotesFromClipboard];
    setNotes(updatedNotes);
    onNotesChange(updatedNotes);
    
    // Select the newly pasted notes
    const newSelection = new Set();
    for (let i = notes.length; i < updatedNotes.length; i++) {
      newSelection.add(i);
    }
    setSelectedNotes(newSelection);
    setContextMenu(null);
  };
  
  // Duplicate selected notes (immediately paste after selection)
  const duplicateSelection = () => {
    if (selectedNotes.size === 0) return;
    
    const selectedNotesList = notes.filter((_, i) => selectedNotes.has(i));
    if (selectedNotesList.length === 0) return;
    
    // Find the beat range of selected notes
    const beats = selectedNotesList.map(n => n.measure * beatsPerMeasure + (n.beat || 0));
    const minBeat = Math.min(...beats);
    const maxBeat = Math.max(...beats);
    const duration = maxBeat - minBeat + 1; // Duration of selection + 1 beat gap
    
    // Create duplicates offset by the duration
    const duplicatedNotes = selectedNotesList.map(note => {
      const currentBeat = note.measure * beatsPerMeasure + (note.beat || 0);
      const newTotalBeat = currentBeat + duration;
      const measure = Math.floor(newTotalBeat / beatsPerMeasure);
      const beat = newTotalBeat % beatsPerMeasure;
      
      return { ...note, measure, beat };
    });
    
    const updatedNotes = [...notes, ...duplicatedNotes];
    setNotes(updatedNotes);
    onNotesChange(updatedNotes);
    
    // Select the duplicated notes
    const newSelection = new Set();
    for (let i = notes.length; i < updatedNotes.length; i++) {
      newSelection.add(i);
    }
    setSelectedNotes(newSelection);
    setContextMenu(null);
  };
  
  // Handle right-click context menu
  const handleContextMenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Find if there's a note at the click position
    const grid = screenToGrid(e.clientX, e.clientY);
    if (grid && grid.x >= 0) {
      const { cellX, cellY } = grid;
      if (cellY >= 0 && cellY < displayRows.length) {
        const noteAtPos = findNoteAtPosition(cellX, cellY);
        
        // If right-clicked on a note, select it (if not already selected)
        if (noteAtPos >= 0) {
          if (!selectedNotes.has(noteAtPos)) {
            setSelectedNotes(new Set([noteAtPos]));
          }
          // Show context menu
          setContextMenu({ x: e.clientX, y: e.clientY });
          return;
        }
      }
    }
    
    // If notes are selected (from marquee or previous selection), show menu
    if (selectedNotes.size > 0) {
      setContextMenu({ x: e.clientX, y: e.clientY });
    }
  };
  
  // Close context menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setContextMenu(null);
    if (contextMenu) {
      window.addEventListener('click', handleClickOutside);
      return () => window.removeEventListener('click', handleClickOutside);
    }
  }, [contextMenu]);
  
  // Update note velocity
  const updateNoteVelocity = (noteIndex, newVelocity) => {
    const clampedVelocity = Math.max(0.1, Math.min(1, newVelocity));
    const newNotes = notes.map((note, i) => {
      if (i === noteIndex) {
        return { ...note, velocity: clampedVelocity };
      }
      return note;
    });
    setNotes(newNotes);
    onNotesChange(newNotes);
  };
  
  // Update note volume
  const updateNoteVolume = (noteIndex, newVolume) => {
    const clampedVolume = Math.max(0, Math.min(1, newVolume));
    const newNotes = notes.map((note, i) => {
      if (i === noteIndex) {
        return { ...note, noteVolume: clampedVolume };
      }
      return note;
    });
    setNotes(newNotes);
    onNotesChange(newNotes);
  };
  
  // Handle velocity/volume bar drag
  const handleLaneBarDrag = (e, noteIndex) => {
    e.preventDefault();
    const startY = e.clientY;
    const note = notes[noteIndex];
    const startValue = laneMode === 'velocity' 
      ? (note?.velocity || 0.8) 
      : (note?.noteVolume ?? 1);
    
    const onMouseMove = (moveEvent) => {
      const deltaY = startY - moveEvent.clientY; // Inverted: drag up = higher value
      const deltaValue = deltaY / velocityLaneHeight;
      if (laneMode === 'velocity') {
        updateNoteVelocity(noteIndex, startValue + deltaValue);
      } else {
        updateNoteVolume(noteIndex, startValue + deltaValue);
      }
    };
    
    const onMouseUp = () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      setEditingVelocityNote(null);
    };
    
    setEditingVelocityNote(noteIndex);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };
  
  // Handle click on lane background to set value directly
  const handleLaneClick = (e, noteIndex) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const clickY = e.clientY - rect.top;
    const newValue = 1 - (clickY / velocityLaneHeight);
    if (laneMode === 'velocity') {
      updateNoteVelocity(noteIndex, newValue);
    } else {
      updateNoteVolume(noteIndex, newValue);
    }
  };
  
  // Select all notes
  const selectAll = () => {
    setSelectedNotes(new Set(notes.map((_, i) => i)));
  };
  
  // Handle loop region mouse down
  const handleLoopMouseDown = (e, type) => {
    e.stopPropagation();
    setIsDraggingLoop(true);
    setLoopDragType(type);
    setLoopDragStartX(e.clientX);
  };
  
  // Click on loop bar to set loop region
  const handleLoopBarClick = (e) => {
    if (!loopBarRef.current || !setLoopStart || !setLoopEnd || !setLoopOn) return;
    const rect = loopBarRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left - keyboardWidth;
    if (x < 0) return;
    const barWidth = beatsPerMeasure * subdivisions * cellWidth;
    const measure = Math.floor(x / barWidth) + 1;
    
    if (e.shiftKey && loopOn) {
      if (measure < loopStart) setLoopStart(measure);
      else if (measure > loopEnd) setLoopEnd(measure);
    } else if (e.altKey || e.metaKey) {
      setLoopStart(measure);
      setLoopEnd(measure + 4); // No upper limit
      if (!loopOn) setLoopOn(true);
    }
  };
  
  // Handle loop region mouse move - MUST be before conditional returns
  useEffect(() => {
    if (!isDraggingLoop || !setLoopStart || !setLoopEnd) return;
    
    const handleMouseMove = (e) => {
      const delta = e.clientX - loopDragStartX;
      const measureDelta = Math.round(delta / cellWidth);
      
      if (measureDelta === 0) return;
      
      if (loopDragType === 'start') {
        const newStart = Math.max(1, Math.min(loopEnd - 1, loopStart + measureDelta));
        setLoopStart(newStart);
      } else if (loopDragType === 'end') {
        // No upper limit - can extend infinitely
        const newEnd = Math.max(loopStart + 1, loopEnd + measureDelta);
        setLoopEnd(newEnd);
      } else if (loopDragType === 'region') {
        const regionLength = loopEnd - loopStart;
        let newStart = loopStart + measureDelta;
        let newEnd = loopEnd + measureDelta;
        
        if (newStart < 1) {
          newStart = 1;
          newEnd = 1 + regionLength;
        }
        // No upper limit for region drag
        
        setLoopStart(newStart);
        setLoopEnd(newEnd);
      }
      
      setLoopDragStartX(e.clientX);
    };
    
    const handleMouseUp = () => {
      setIsDraggingLoop(false);
      setLoopDragType(null);
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDraggingLoop, loopDragType, loopDragStartX, loopStart, loopEnd, cellWidth, measures, setLoopStart, setLoopEnd]);
  
  if (!track) {
    return (
      <div className="flex-1 flex items-center justify-center bg-[#0d0d0d] text-gray-500">
        <div className="text-center">
          <Piano className="w-16 h-16 mx-auto mb-4 opacity-30" />
          <p>Select a track to edit in Piano Roll</p>
        </div>
      </div>
    );
  }
  
  if (isKitTrack && kitSamplesList.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center bg-[#0d0d0d] text-gray-500">
        <div className="text-center">
          <Package className="w-16 h-16 mx-auto mb-4 opacity-30" />
          <p className="mb-2">No samples in this kit</p>
          <p className="text-sm">Upload samples in Kit Sample Manager</p>
        </div>
      </div>
    );
  }
  
  // Marquee rect for display - positioned relative to the grid area (after keyboard)
  const marqueeRect = isMarquee && marqueeStart && marqueeEnd ? {
    left: Math.min(marqueeStart.x, marqueeEnd.x),
    top: Math.min(marqueeStart.y, marqueeEnd.y),
    width: Math.abs(marqueeEnd.x - marqueeStart.x),
    height: Math.abs(marqueeEnd.y - marqueeStart.y)
  } : null;
  
  return (
    <div className="flex-1 flex flex-col bg-[#0d0d0d] overflow-hidden" tabIndex={0}>
      {/* Toolbar */}
      <div className="h-10 bg-[#1a1a1a] border-b border-[#333] flex items-center px-3 gap-3 shrink-0">
        {/* Tools */}
        <div className="flex items-center gap-1 bg-[#0a0a0a] rounded p-1">
          <button 
            onClick={() => setTool('draw')} 
            className={`p-1.5 rounded ${tool === 'draw' ? 'bg-green-500 text-white' : 'text-gray-400 hover:text-white'}`} 
            title="Pencil (P) - Click to write notes, Double-click to erase"
            data-testid="draw-tool"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
          </button>
          <button 
            onClick={() => setTool('move')} 
            className={`p-1.5 rounded ${tool === 'move' ? 'bg-blue-500 text-white' : 'text-gray-400 hover:text-white'}`} 
            title="Hand (H) - Drag to move selected notes"
            data-testid="move-tool"
          >
            <Hand className="w-4 h-4" />
          </button>
          <button 
            onClick={() => setTool('select')} 
            className={`p-1.5 rounded ${tool === 'select' ? 'bg-purple-500 text-white' : 'text-gray-400 hover:text-white'}`} 
            title="Selection (S) - Click to select, drag for marquee"
            data-testid="select-tool"
          >
            <Crosshair className="w-4 h-4" />
          </button>
          <button 
            onClick={() => setTool('erase')} 
            className={`p-1.5 rounded ${tool === 'erase' ? 'bg-red-500 text-white' : 'text-gray-400 hover:text-white'}`} 
            title="Erase (E) - Click notes to delete"
            data-testid="erase-tool"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
        
        <div className="w-px h-6 bg-[#333]" />
        
        {/* Note Duration Selector - Prominent dropdown style */}
        <div className="flex items-center gap-2 bg-[#1a1a1a] rounded-lg px-3 py-1.5 border border-[#333]" data-testid="note-duration-selector">
          <span className="text-xs text-amber-400 font-semibold whitespace-nowrap">NOTE SIZE:</span>
          <div className="flex items-center gap-0.5 bg-[#0a0a0a] rounded-md p-0.5">
            {noteDurations.map(dur => (
              <button
                key={dur.value}
                onClick={() => setNoteDuration(dur.value)}
                data-testid={`note-duration-${dur.value}`}
                className={`px-2 py-1 text-xs rounded-md transition-all ${
                  noteDuration === dur.value 
                    ? 'bg-amber-500 text-black font-bold shadow-lg shadow-amber-500/30' 
                    : 'text-gray-400 hover:text-white hover:bg-white/10'
                }`}
                title={`${dur.label} note (${dur.beats} beats)`}
              >
                {dur.label}
              </button>
            ))}
          </div>
        </div>
        
        <div className="w-px h-6 bg-[#333]" />
        
        {/* Grid Resolution Selector - Controls snap points and visible grid lines */}
        <div className="flex items-center gap-2 bg-[#1a1a1a] rounded-lg px-3 py-1.5 border border-[#333]" data-testid="grid-resolution-selector">
          <span className="text-xs text-cyan-400 font-semibold whitespace-nowrap" title="Snap & Grid (⌘/Ctrl + 1-5)">GRID:</span>
          <div className="flex items-center gap-0.5 bg-[#0a0a0a] rounded-md p-0.5">
            {gridResolutions.map((res, idx) => (
              <button
                key={res.value}
                onClick={() => setGridResolution(res.value)}
                data-testid={`grid-resolution-${res.value}`}
                className={`px-2 py-1 text-xs rounded-md transition-all ${
                  gridResolution === res.value 
                    ? 'bg-cyan-500 text-black font-bold shadow-lg shadow-cyan-500/30' 
                    : 'text-gray-400 hover:text-white hover:bg-white/10'
                }`}
                title={`${res.desc} (⌘${idx + 1})`}
              >
                {res.label}
              </button>
            ))}
          </div>
          
          {/* Snap Toggle */}
          <button
            onClick={() => setSnapEnabled(!snapEnabled)}
            data-testid="snap-toggle"
            className={`ml-2 px-2 py-1 text-xs rounded-md font-semibold transition-all ${
              snapEnabled 
                ? 'bg-green-500 text-black shadow-lg shadow-green-500/30' 
                : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
            }`}
            title={`Snap to Grid (${snapEnabled ? 'ON' : 'OFF'})`}
          >
            SNAP
          </button>
        </div>
        
        <div className="w-px h-6 bg-[#333]" />
        
        {/* Selection actions */}
        <div className="flex items-center gap-1">
          <button 
            onClick={selectAll} 
            className="px-2 py-1 text-xs text-gray-400 hover:text-white hover:bg-white/10 rounded"
            title="Select All (Cmd+A)"
          >
            Select All
          </button>
          <button 
            onClick={copySelection} 
            disabled={selectedNotes.size === 0}
            className={`p-1.5 rounded ${selectedNotes.size > 0 ? 'text-gray-400 hover:text-white hover:bg-white/10' : 'text-gray-600 cursor-not-allowed'}`}
            title="Copy (Cmd+C)"
          >
            <Copy className="w-3.5 h-3.5" />
          </button>
          <button 
            onClick={cutSelection} 
            disabled={selectedNotes.size === 0}
            className={`p-1.5 rounded ${selectedNotes.size > 0 ? 'text-gray-400 hover:text-white hover:bg-white/10' : 'text-gray-600 cursor-not-allowed'}`}
            title="Cut (Cmd+X)"
          >
            <Scissors className="w-3.5 h-3.5" />
          </button>
          <button 
            onClick={pasteClipboard} 
            disabled={clipboard.length === 0}
            className={`p-1.5 rounded ${clipboard.length > 0 ? 'text-gray-400 hover:text-white hover:bg-white/10' : 'text-gray-600 cursor-not-allowed'}`}
            title="Paste (Cmd+V)"
          >
            <ClipboardPaste className="w-3.5 h-3.5" />
          </button>
          <button 
            onClick={duplicateSelection} 
            disabled={selectedNotes.size === 0}
            className={`px-2 py-1 text-xs rounded ${selectedNotes.size > 0 ? 'text-blue-400 hover:text-blue-300 hover:bg-blue-500/20' : 'text-gray-600 cursor-not-allowed'}`}
            title="Duplicate (Cmd+D)"
          >
            Dup
          </button>
          <button 
            onClick={deleteSelection} 
            disabled={selectedNotes.size === 0}
            className={`px-2 py-1 text-xs rounded ${selectedNotes.size > 0 ? 'text-red-400 hover:text-red-300 hover:bg-red-500/20' : 'text-gray-600 cursor-not-allowed'}`}
            title="Delete (Delete/Backspace)"
          >
            Del ({selectedNotes.size})
          </button>
        </div>
        
        <div className="w-px h-6 bg-[#333]" />
        
        {/* Zoom Controls */}
        {/* Zoom Controls with keyboard hints */}
        <div className="flex items-center gap-1 text-xs text-gray-400" title="Horizontal Zoom (⌥/Alt + Scroll)">
          <span>H:</span>
          <button onClick={() => onZoomChange && onZoomChange(Math.max(0.01, zoom * 0.5))} className="p-1 hover:bg-white/10 rounded">
            <ZoomOut className="w-3.5 h-3.5" />
          </button>
          <span className="w-10 text-center text-white">{Math.round(zoom * 100)}%</span>
          <button onClick={() => onZoomChange && onZoomChange(Math.min(4, zoom * 2))} className="p-1 hover:bg-white/10 rounded">
            <ZoomIn className="w-3.5 h-3.5" />
          </button>
        </div>
        
        <div className="flex items-center gap-1 text-xs text-gray-400" title="Vertical Zoom (⌥/Alt + ⇧ + Scroll)">
          <span>V:</span>
          <button onClick={() => setVerticalZoom(Math.max(0.1, verticalZoom * 0.5))} className="p-1 hover:bg-white/10 rounded">
            <ZoomOut className="w-3.5 h-3.5" />
          </button>
          <span className="w-10 text-center text-white">{Math.round(verticalZoom * 100)}%</span>
          <button onClick={() => setVerticalZoom(Math.min(4, verticalZoom * 2))} className="p-1 hover:bg-white/10 rounded">
            <ZoomIn className="w-3.5 h-3.5" />
          </button>
        </div>
        
        <div className="w-px h-6 bg-[#333]" />
        
        <div className="text-sm text-gray-400">
          <span className="text-white font-medium">{track.name}</span>
          {isKitTrack && <span className="ml-2 text-purple-400 text-xs">({kitSamplesList.length} samples)</span>}
          <span className="ml-2">&bull; {notes.length} notes</span>
          {selectedNotes.size > 0 && <span className="ml-2 text-blue-400">&bull; {selectedNotes.size} selected</span>}
        </div>
        
        <div className="w-px h-6 bg-[#333]" />
        
        {/* Metronome Toggle */}
        <button
          onClick={() => setMetronomeOn(!metronomeOn)}
          className={`p-1.5 rounded flex items-center gap-1 ${metronomeOn ? 'bg-blue-500 text-white' : 'text-gray-400 hover:text-white hover:bg-white/10'}`}
          title={`Metronome (${metronomeOn ? 'ON' : 'OFF'}) - Click beat on playback`}
          data-testid="piano-roll-metronome"
        >
          <Clock className="w-4 h-4" />
          <span className="text-xs">{metronomeOn ? 'ON' : 'OFF'}</span>
        </button>
        
        <div className="w-px h-6 bg-[#333]" />
        
        {/* Velocity/Volume Lane Toggle */}
        <div className="flex items-center gap-1 bg-[#0a0a0a] rounded p-0.5">
          <button
            onClick={() => { setShowVelocityLane(true); setLaneMode('velocity'); }}
            className={`px-2 py-1 text-xs rounded ${showVelocityLane && laneMode === 'velocity' ? 'bg-amber-600 text-white' : 'text-gray-400 hover:text-white'}`}
            title="Velocity Lane"
            data-testid="velocity-toggle"
          >
            <Activity className="w-3.5 h-3.5 inline mr-1" />
            Vel
          </button>
          <button
            onClick={() => { setShowVelocityLane(true); setLaneMode('volume'); }}
            className={`px-2 py-1 text-xs rounded ${showVelocityLane && laneMode === 'volume' ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white'}`}
            title="Volume Lane"
            data-testid="volume-toggle"
          >
            <Volume2 className="w-3.5 h-3.5 inline mr-1" />
            Vol
          </button>
          {showVelocityLane && (
            <button
              onClick={() => setShowVelocityLane(false)}
              className="px-1.5 py-1 text-xs text-gray-500 hover:text-white rounded"
              title="Hide Lane"
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </div>
        
        <div className="flex-1" />
        
        {/* Quick tips */}
        <div className="text-[10px] text-gray-500">
          {tool === 'draw' && '✏️ Pencil: Click to write • Double-click to erase • Drag edge to resize'}
          {tool === 'move' && '👆 Hand: Drag selected notes to move them'}
          {tool === 'select' && '✛ Selection: Click to select • Drag for marquee • Shift+click to add'}
          {tool === 'erase' && '🗑️ Erase: Click notes to delete'}
        </div>
      </div>
      
      {/* Loop Region Bar / Bar Counter */}
      <div 
        ref={loopBarRef}
        className="h-8 bg-[#1a1a1a] border-b border-[#444] flex relative cursor-pointer shrink-0"
        onClick={handleLoopBarClick}
        title="Alt+Click to set loop start • Shift+Click to extend"
      >
        {/* Keyboard spacer - shows BAR label */}
        <div style={{ width: keyboardWidth }} className="bg-[#0a0a0a] border-r border-[#333] flex items-center justify-center">
          <span className="text-[10px] text-gray-500 font-medium">BAR</span>
          {loopOn && <Repeat className="w-3 h-3 text-yellow-500 ml-1" />}
        </div>
        
        {/* Measure/Bar markers with loop overlay */}
        <div className="flex-1 flex relative overflow-hidden">
          {/* Each bar = beatsPerMeasure * subdivisions cells */}
          {Array.from({ length: measures }).map((_, i) => {
            const barWidth = beatsPerMeasure * subdivisions * cellWidth;
            return (
              <div 
                key={i} 
                className={`border-r flex items-center justify-center font-mono ${
                  (i + 1) % 4 === 0 ? 'border-[#555] bg-[#222]' : 'border-[#333]'
                }`}
                style={{ width: barWidth, minWidth: barWidth }}
              >
                <span className={`text-sm ${
                  (i + 1) % 4 === 1 ? 'text-white font-bold' : 'text-gray-500'
                }`}>
                  {i + 1}
                </span>
              </div>
            );
          })}
          
          {/* Loop Region Overlay */}
          {loopOn && setLoopStart && setLoopEnd && (
            <div
              className="absolute top-0 h-full bg-yellow-500/40 border-l-2 border-r-2 border-yellow-500 flex items-center pointer-events-auto"
              style={{
                left: (loopStart - 1) * beatsPerMeasure * subdivisions * cellWidth,
                width: (loopEnd - loopStart) * beatsPerMeasure * subdivisions * cellWidth,
              }}
            >
              {/* Left handle */}
              <div
                className="absolute left-0 top-0 bottom-0 w-2 bg-yellow-500 cursor-ew-resize hover:bg-yellow-400 flex items-center justify-center"
                onMouseDown={(e) => handleLoopMouseDown(e, 'start')}
              >
                <div className="w-0.5 h-3 bg-yellow-800 rounded" />
              </div>
              
              {/* Center drag area */}
              <div
                className="flex-1 h-full cursor-move flex items-center justify-center"
                onMouseDown={(e) => handleLoopMouseDown(e, 'region')}
              >
                <span className="text-[10px] text-yellow-900 font-medium select-none">
                  {loopStart}-{loopEnd}
                </span>
              </div>
              
              {/* Right handle */}
              <div
                className="absolute right-0 top-0 bottom-0 w-2 bg-yellow-500 cursor-ew-resize hover:bg-yellow-400 flex items-center justify-center"
                onMouseDown={(e) => handleLoopMouseDown(e, 'end')}
              >
                <div className="w-0.5 h-3 bg-yellow-800 rounded" />
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Grid */}
      <div 
        ref={gridRef} 
        className="flex-1 min-h-0 overflow-auto relative select-none" 
        style={{ 
          cursor: isResizing ? 'ew-resize' : 
                  isHoveringEdge ? 'ew-resize' :
                  tool === 'move' ? 'grab' : 
                  tool === 'draw' ? 'url(\"data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'24\' height=\'24\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%2322c55e\' stroke-width=\'2\'%3E%3Cpath d=\'M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z\'/%3E%3C/svg%3E\") 2 22, crosshair' : 
                  tool === 'select' ? 'crosshair' : 
                  'not-allowed' 
        }}
        onMouseDown={handleGridMouseDown}
        onDoubleClick={handleGridDoubleClick}
        onMouseMove={handleGridHover}
        onContextMenu={handleContextMenu}
      >
        <div className="flex relative" style={{ minWidth: keyboardWidth + totalCells * cellWidth, minHeight: displayRows.length * cellHeight }}>
          {/* Keyboard / Sample Labels */}
          <div className="sticky left-0 z-20 bg-[#1a1a1a]" style={{ width: keyboardWidth }}>
            {displayRows.map((row, i) => {
              if (isKitTrack) {
                const sample = row;
                return (
                  <div 
                    key={sample.id} 
                    onClick={() => playPreview(i)} 
                    className="flex items-center gap-1 px-1 text-[9px] font-medium cursor-pointer border-b border-[#333] bg-purple-900/30 hover:bg-purple-800/40 text-purple-200 truncate"
                    style={{ height: cellHeight }}
                    title={sample.filename}
                  >
                    <div className="w-2 h-2 rounded-full bg-purple-500 shrink-0" />
                    <span className="truncate">{sample.filename?.replace(/\.[^/.]+$/, '').slice(0, 15)}</span>
                  </div>
                );
              } else {
                const note = row;
                return (
                  <div 
                    key={note} 
                    onClick={() => playPreview(i)} 
                    className={`flex items-center justify-end pr-1 text-[9px] font-mono cursor-pointer border-b border-[#333] ${isBlackKey(note) ? 'bg-[#222] text-gray-500' : 'bg-[#444] text-white'}`} 
                    style={{ height: cellHeight }}
                  >
                    {note}
                  </div>
                );
              }
            })}
          </div>
          
          {/* Grid Background */}
          <div className="relative flex-1">
            {/* Row backgrounds */}
            {displayRows.map((row, i) => (
              <div 
                key={`row-${i}`} 
                className={`absolute left-0 right-0 border-b ${isKitTrack ? (i % 2 === 0 ? 'bg-purple-900/10' : 'bg-purple-900/5') : (isBlackKey(row) ? 'bg-[#111]' : 'bg-[#181818]')} border-[#222]`} 
                style={{ top: i * cellHeight, height: cellHeight }} 
              />
            ))}
            
            {/* Column lines - visibility controlled by gridResolution */}
            {Array.from({ length: totalCells + 1 }).map((_, i) => {
              const isMeasure = i % (beatsPerMeasure * subdivisions) === 0;
              const isBeat = i % subdivisions === 0;
              // Grid lines based on resolution: gridResolution determines finest visible lines
              // subdivisions=16 total, so gridResolution=4 means show every 4th subdivision (16th notes)
              const gridStep = subdivisions / gridResolution; // How many cells between grid lines
              const isGridLine = i % gridStep === 0;
              
              // Only render visible lines for performance
              if (!isMeasure && !isBeat && !isGridLine) return null;
              
              let lineClass = 'border-l border-[#1a1a1a]'; // Finest subdivision (barely visible)
              if (isMeasure) {
                lineClass = 'border-l-2 border-[#555]'; // Measure lines - brightest, thicker
              } else if (isBeat) {
                lineClass = 'border-l border-[#3a3a3a]'; // Beat lines - medium brightness
              } else if (isGridLine) {
                lineClass = 'border-l border-[#252525]'; // Grid subdivision lines
              }
              
              return (
                <div 
                  key={`col-${i}`} 
                  className={`absolute top-0 bottom-0 ${lineClass}`} 
                  style={{ left: i * cellWidth }} 
                />
              );
            })}
            
            {/* Notes */}
            {notes.map((note, i) => {
              const pos = getNotePosition(note);
              if (pos.rowIndex < 0) return null;
              
              const isSelected = selectedNotes.has(i);
              const isBeingDragged = isDragging && isSelected;
              
              const noteLeft = pos.cellX * cellWidth + 1 + (isBeingDragged ? dragOffsetState.x : 0);
              const noteTop = pos.rowIndex * cellHeight + 1 + (isBeingDragged ? dragOffsetState.y : 0);
              const noteWidth = Math.max(pos.durBeats * subdivisions * cellWidth - 2, cellWidth - 2);
              
              return (
                <motion.div
                  key={i}
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: isBeingDragged ? 0.7 : 1 }}
                  className={`absolute rounded-sm ${tool === 'select' ? 'cursor-move' : 'cursor-pointer'}`}
                  style={{
                    left: noteLeft,
                    top: noteTop,
                    width: noteWidth,
                    height: cellHeight - 2,
                    backgroundColor: isBeingDragged ? '#fff' : isSelected ? '#fff' : track.color,
                    border: isSelected ? '2px solid #3b82f6' : `1px solid ${track.color}`,
                    zIndex: isBeingDragged ? 100 : isSelected ? 50 : 10,
                    boxShadow: isBeingDragged ? '0 4px 12px rgba(0,0,0,0.5)' : isSelected ? '0 0 0 1px #3b82f6' : 'none',
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    if (tool === 'select') {
                      if (e.shiftKey) {
                        setSelectedNotes(prev => {
                          const next = new Set(prev);
                          if (next.has(i)) next.delete(i);
                          else next.add(i);
                          return next;
                        });
                      } else if (!selectedNotes.has(i)) {
                        setSelectedNotes(new Set([i]));
                      }
                      setIsDragging(true);
                      setDragStart({ x: e.clientX, y: e.clientY });
                      dragOffsetRef.current = { x: 0, y: 0 };
                      setDragOffsetState({ x: 0, y: 0 });
                    } else if (tool === 'draw' || tool === 'erase') {
                      const newNotes = notes.filter((_, idx) => idx !== i);
                      setNotes(newNotes);
                      onNotesChange(newNotes);
                      setSelectedNotes(new Set());
                    }
                  }}
                />
              );
            })}
            
            {/* Marquee selection box */}
            {marqueeRect && (
              <div 
                className="absolute border border-blue-500 bg-blue-500/20 pointer-events-none"
                style={{
                  left: marqueeRect.left,
                  top: marqueeRect.top,
                  width: marqueeRect.width,
                  height: marqueeRect.height,
                  zIndex: 1000
                }}
              />
            )}
          </div>
        </div>
      </div>
      
      {/* Velocity/Volume Lane */}
      {showVelocityLane && notes.length > 0 && (
        <div className="border-t border-[#333] bg-[#0a0a0a] shrink-0" style={{ height: velocityLaneHeight + 24 }}>
          {/* Lane Header */}
          <div className="h-6 bg-[#1a1a1a] border-b border-[#333] flex items-center px-3 text-xs text-gray-400">
            {laneMode === 'velocity' ? (
              <>
                <Activity className="w-3 h-3 mr-2 text-amber-500" />
                <span className="text-amber-400 font-medium">Velocity</span>
              </>
            ) : (
              <>
                <Volume2 className="w-3 h-3 mr-2 text-blue-500" />
                <span className="text-blue-400 font-medium">Volume</span>
              </>
            )}
            <span className="ml-2 text-gray-600">• Drag bars up/down or click to set</span>
            <div className="flex-1" />
            <span className="text-gray-600">0%</span>
            <div className={`w-12 h-1 rounded mx-2 ${laneMode === 'velocity' ? 'bg-gradient-to-r from-red-500 via-yellow-500 to-green-500' : 'bg-gradient-to-r from-blue-900 via-blue-500 to-blue-300'}`} />
            <span className="text-gray-600">100%</span>
          </div>
          
          {/* Bars */}
          <div className="relative overflow-x-auto overflow-y-hidden" style={{ height: velocityLaneHeight }}>
            <div className="relative" style={{ width: keyboardWidth + totalCells * cellWidth, height: '100%' }}>
              {/* Grid lines */}
              <div className="absolute inset-0" style={{ left: keyboardWidth }}>
                {Array.from({ length: totalCells + 1 }).map((_, i) => {
                  const isMeasure = i % (beatsPerMeasure * subdivisions) === 0;
                  const isBeat = i % subdivisions === 0;
                  return (
                    <div 
                      key={`vel-col-${i}`} 
                      className={`absolute top-0 bottom-0 ${isMeasure ? 'border-l border-[#333]' : isBeat ? 'border-l border-[#222]' : ''}`} 
                      style={{ left: i * cellWidth }} 
                    />
                  );
                })}
              </div>
              
              {/* Label on the left */}
              <div className="absolute left-0 top-0 bottom-0 bg-[#1a1a1a] flex flex-col justify-between py-1 text-[9px] text-gray-500 pr-2" style={{ width: keyboardWidth }}>
                <span className="text-right w-full">100%</span>
                <span className={`text-right w-full ${laneMode === 'velocity' ? 'text-amber-500/70' : 'text-blue-500/70'}`}>
                  {laneMode === 'velocity' ? 'Velocity' : 'Volume'}
                </span>
                <span className="text-right w-full">0%</span>
              </div>
              
              {/* Bars for each note */}
              <div className="absolute top-0 bottom-0" style={{ left: keyboardWidth }}>
                {notes.map((note, i) => {
                  const beat = note.measure * beatsPerMeasure + (note.beat || 0);
                  const cellX = beat * subdivisions;
                  const value = laneMode === 'velocity' 
                    ? (note.velocity || 0.8) 
                    : (note.noteVolume ?? 1);
                  const barHeight = value * velocityLaneHeight;
                  const isSelected = selectedNotes.has(i);
                  const isEditing = editingVelocityNote === i;
                  
                  // Color based on value and mode
                  const getBarColor = (v) => {
                    if (laneMode === 'velocity') {
                      if (v < 0.33) return '#ef4444'; // red
                      if (v < 0.66) return '#eab308'; // yellow
                      return '#22c55e'; // green
                    } else {
                      // Volume: blue gradient
                      if (v < 0.33) return '#1e3a5f'; // dark blue
                      if (v < 0.66) return '#3b82f6'; // blue
                      return '#93c5fd'; // light blue
                    }
                  };
                  
                  return (
                    <div
                      key={`lane-${i}`}
                      className={`absolute bottom-0 cursor-ns-resize transition-opacity ${isEditing ? 'opacity-100' : 'hover:opacity-80'}`}
                      style={{
                        left: cellX * cellWidth + 2,
                        width: Math.max(cellWidth - 4, 8),
                        height: barHeight,
                        backgroundColor: isSelected ? (laneMode === 'velocity' ? '#f59e0b' : '#3b82f6') : getBarColor(value),
                        borderRadius: '2px 2px 0 0',
                        border: isSelected ? `1px solid ${laneMode === 'velocity' ? '#fbbf24' : '#60a5fa'}` : 'none',
                        boxShadow: isEditing ? '0 0 8px rgba(255,255,255,0.3)' : 'none'
                      }}
                      onMouseDown={(e) => handleLaneBarDrag(e, i)}
                      onClick={(e) => { e.stopPropagation(); handleLaneClick(e, i); }}
                      title={`${laneMode === 'velocity' ? 'Velocity' : 'Volume'}: ${Math.round(value * 100)}%`}
                    >
                      {/* Value label */}
                      {(isSelected || isEditing || cellWidth > 15) && (
                        <div className="absolute -top-4 left-1/2 -translate-x-1/2 text-[9px] text-white font-medium whitespace-nowrap">
                          {Math.round(value * 100)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Context Menu (Right-click) */}
      {contextMenu && selectedNotes.size > 0 && (
        <div 
          className="fixed bg-[#1a1a1a] border border-[#444] rounded-lg shadow-xl py-1 z-50 min-w-[160px]"
          style={{ left: contextMenu.x, top: contextMenu.y }}
          onClick={(e) => e.stopPropagation()}
        >
          <button 
            onClick={copySelection}
            className="w-full px-3 py-2 text-left text-sm text-gray-300 hover:bg-white/10 flex items-center gap-2"
          >
            <Copy className="w-4 h-4" /> Copy <span className="ml-auto text-gray-500 text-xs">⌘C</span>
          </button>
          <button 
            onClick={cutSelection}
            className="w-full px-3 py-2 text-left text-sm text-gray-300 hover:bg-white/10 flex items-center gap-2"
          >
            <Scissors className="w-4 h-4" /> Cut <span className="ml-auto text-gray-500 text-xs">⌘X</span>
          </button>
          <button 
            onClick={pasteClipboard}
            disabled={clipboard.length === 0}
            className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 ${clipboard.length > 0 ? 'text-gray-300 hover:bg-white/10' : 'text-gray-600 cursor-not-allowed'}`}
          >
            <ClipboardPaste className="w-4 h-4" /> Paste <span className="ml-auto text-gray-500 text-xs">⌘V</span>
          </button>
          <div className="border-t border-[#333] my-1" />
          <button 
            onClick={duplicateSelection}
            className="w-full px-3 py-2 text-left text-sm text-gray-300 hover:bg-white/10 flex items-center gap-2"
          >
            <Copy className="w-4 h-4" /> Duplicate <span className="ml-auto text-gray-500 text-xs">⌘D</span>
          </button>
          <div className="border-t border-[#333] my-1" />
          <button 
            onClick={deleteSelection}
            className="w-full px-3 py-2 text-left text-sm text-red-400 hover:bg-red-500/20 flex items-center gap-2"
          >
            <Trash2 className="w-4 h-4" /> Delete <span className="ml-auto text-gray-500 text-xs">⌫</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminScoreCreator;
export { AdminScoreCreator as StudioDAW };
